<template>
  <AuthenticatedView
    :loading="baseLoading && !baseResult"
    :current-user="currentUser"
    :has-result="!!baseResult"
  >
    <AppHeader
      v-if="currentUser"
      :user="currentUser"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div :class="['card', { 'full-width': isFullWidth }]">
      <breadcrumbs :items="breadcrumbsItems" />
      <archived-project-warning v-if="!loading && !error && project" :archived="projectArchived" />
      <div v-if="loading">{{ __('Loading…') }}</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="project" class="dashboard">
        <h2 class="dashboard-page-title">
          {{ s__('ProjectSettings|Project Settings') }} {{ project.title }}
        </h2>

        <button
          v-if="isMobileLayout"
          type="button"
          class="btn-link-secondary mobile-settings-nav-toggle"
          @click="toggleMobileSidebar"
        >
          {{
            isMobileSidebarExpanded
              ? s__('ProjectSettings|Hide settings menu')
              : s__('ProjectSettings|Show settings menu')
          }}
        </button>

        <!-- Settings section -->
        <div
          class="dashboard-layout"
          :class="{ 'is-sidebar-collapsed': isSidebarCollapsed && canCollapseSidebar }"
          :style="{ '--dashboard-sticky-top': '12px' }"
        >
          <SettingsSidebarNav
            v-show="!isMobileLayout || isMobileSidebarExpanded"
            :items="projectSidebarItems"
            :active-panel="activeSettingsSection"
            :collapsed="isSidebarCollapsed && canCollapseSidebar"
            :can-collapse="canCollapseSidebar"
            :is-full-width="isFullWidth"
            :can-toggle-full-width="canToggleFullWidth"
            @toggle-panel="openProjectSection"
            @toggle-collapse="toggleSidebarCollapse"
            @toggle-full-width="toggleFullWidth"
          />
          <section class="dashboard-panel">
            <header class="dashboard-panel-header">
              <div>
                <h3 class="dashboard-panel-title">{{ s__('ProjectSettings|Settings') }}</h3>
                <p class="dashboard-panel-subtitle">{{ activeProjectPanelTitle }}</p>
              </div>
              <div v-if="showHeaderCreateAction" class="header-actions">
                <button
                  type="button"
                  class="btn-link dashboard-create-task-btn dashboard-header-create-btn"
                  :aria-label="headerCreateActionLabel"
                  @click="handleHeaderCreateAction"
                >
                  <span class="dashboard-create-task-label">{{ headerCreateActionLabel }}</span>
                  <span class="dashboard-create-task-plus" aria-hidden="true">+</span>
                </button>
              </div>
            </header>
            <div class="dashboard-panel-body settings-page-content">
              <div class="form">
                <!-- Basic Settings -->
                <div
                  v-show="activeSettingsSection === 'basicSettings'"
                  class="settings-block"
                  data-settings-section="basicSettings"
                >
                  <div v-show="expandedSections.basicSettings" class="section-content">
                    <AlertBanner
                      v-if="basicSettingsAlert"
                      :type="basicSettingsAlert.type"
                      :title="basicSettingsAlert.title"
                      :message="basicSettingsAlert.message"
                      @dismiss="basicSettingsAlert = null"
                    />
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Title') }}</label>
                      <div
                        v-if="!editingTitle"
                        class="project-title-display-row"
                        style="display: flex; align-items: center; gap: 8px"
                      >
                        <div style="font-weight: 600">{{ project.title }}</div>
                        <button
                          v-if="project.canUpdateTitle"
                          type="button"
                          class="btn-icon project-edit-title-btn"
                          :title="s__('ProjectSettings|Edit title')"
                          @click="startEditTitle"
                        >
                          <EditIcon :size="16" color="#666" />
                        </button>
                      </div>
                      <div v-else class="title-edit-row">
                        <input
                          v-model="editedTitle"
                          type="text"
                          class="input"
                          @keyup.enter="saveTitle"
                          @keyup.esc="cancelEditTitle"
                        />
                        <div class="mobile-stretch-btn-group">
                          <button
                            type="button"
                            class="btn-primary"
                            :disabled="savingTitle || !editedTitle.trim()"
                            @click="saveTitle"
                          >
                            {{
                              savingTitle
                                ? s__('ProjectSettings|Saving...')
                                : s__('ProjectSettings|Save')
                            }}
                          </button>
                          <button type="button" class="btn-secondary" @click="cancelEditTitle">
                            {{ s__('ProjectSettings|Cancel') }}
                          </button>
                        </div>
                      </div>
                      <div v-if="titleError" class="error" style="margin-top: 4px">
                        {{ titleError }}
                      </div>
                    </div>
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Code') }}</label>
                      <input
                        v-if="!codeLocked"
                        v-model="editedCode"
                        type="text"
                        class="input"
                        :disabled="savingCode || !canSetCode"
                        :placeholder="s__('ProjectSettings|Optional short code, e.g. TRCK')"
                        @keyup.enter="saveCode"
                      />
                      <input v-else :value="editedCode" type="text" disabled class="input" />
                      <div v-if="!codeLocked" style="margin-top: 8px">
                        <button
                          type="button"
                          class="btn-primary btn-sm"
                          :disabled="savingCode || !editedCode.trim() || !isCodeFormatValid"
                          @click="saveCode"
                        >
                          {{
                            savingCode
                              ? s__('ProjectSettings|Saving...')
                              : s__('ProjectSettings|Save')
                          }}
                        </button>
                      </div>
                      <div v-if="codeError" class="error" style="margin-top: 4px">
                        {{ codeError }}
                      </div>
                      <div v-else-if="codeLocked" class="help-text" style="color: #d97706">
                        {{ s__('ProjectSettings|Project code is set and cannot be changed.') }}
                      </div>
                      <div v-else class="help-text">
                        {{
                          s__(
                            'ProjectSettings|Optional. 3-12 chars, letters/digits/_/-. Must include at least one letter. Once set, code cannot be changed.'
                          )
                        }}
                      </div>
                    </div>
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Description') }}</label>
                      <div v-if="!editingDescription">
                        <div
                          class="project-description-display-row"
                          style="display: flex; align-items: flex-start; gap: 8px"
                        >
                          <p
                            v-if="project.description"
                            class="muted"
                            style="margin: 0; white-space: pre-wrap"
                          >
                            {{ project.description }}
                          </p>
                          <p v-else class="muted" style="margin: 0; font-style: italic">
                            {{ s__('ProjectSettings|No description') }}
                          </p>
                          <button
                            v-if="project.canUpdateDescription"
                            type="button"
                            class="btn-icon project-edit-description-btn"
                            :title="s__('ProjectSettings|Edit description')"
                            @click="startEditDescription"
                          >
                            <EditIcon :size="16" color="#666" />
                          </button>
                        </div>
                      </div>
                      <div v-else style="margin-top: 8px">
                        <textarea
                          v-model="editedDescription"
                          class="textarea"
                          rows="3"
                          :placeholder="s__('ProjectSettings|Enter project description (optional)')"
                          @keyup.esc="cancelEditDescription"
                        />
                        <div class="mobile-stretch-btn-group" style="margin-top: 8px">
                          <button
                            type="button"
                            class="btn-primary"
                            :disabled="savingDescription"
                            @click="saveDescription"
                          >
                            {{
                              savingDescription
                                ? s__('ProjectSettings|Saving...')
                                : s__('ProjectSettings|Save')
                            }}
                          </button>
                          <button
                            type="button"
                            class="btn-secondary"
                            @click="cancelEditDescription"
                          >
                            {{ s__('ProjectSettings|Cancel') }}
                          </button>
                        </div>
                        <div v-if="descriptionError" class="error" style="margin-top: 4px">
                          {{ descriptionError }}
                        </div>
                      </div>
                    </div>
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Language') }}</label>
                      <select v-model="projectLanguage" :disabled="saving" class="language-select">
                        <option value="en">English</option>
                        <option value="ru">Русский</option>
                      </select>
                      <div class="help-text">
                        {{ s__('ProjectSettings|Language for group chat notifications') }}
                      </div>
                    </div>
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Timezone') }}</label>
                      <select
                        v-model="projectTimezone"
                        :disabled="saving || loadingTimezones"
                        class="timezone-select"
                      >
                        <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
                          {{ tz.label }}
                        </option>
                      </select>
                      <div class="help-text">
                        {{
                          s__(
                            'ProjectSettings|Timezone for displaying task deadlines in group notifications'
                          )
                        }}
                      </div>
                    </div>

                    <h4 class="feature-flags-block-title">{{ s__('ProjectSettings|Features') }}</h4>
                    <div class="feature-flags-mobile-list">
                      <div v-if="!isPersonalProject" class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectGradesEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Task Grades') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showGradesInfoModal = true"
                            :title="s__('ProjectSettings|Task Grades Information')"
                            aria-label="Task Grades Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{ s__('ProjectSettings|Allow grading for finished tasks') }}
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectVerticalRelationsEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Task Hierarchy') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showHierarchyInfoModal = true"
                            :title="s__('ProjectSettings|Task Hierarchy Information')"
                            aria-label="Task Hierarchy Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{
                            s__(
                              'ProjectSettings|Allow parent-child relationships between tasks (up to 3 levels)'
                            )
                          }}
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectTimeTrackingEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Time Tracking') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showTimeTrackingInfoModal = true"
                            :title="s__('ProjectSettings|Time Tracking Information')"
                            aria-label="Time Tracking Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{ s__('ProjectSettings|Allow members to log time spent on tasks') }}
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectTaskCloningEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Task Cloning') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showCloningInfoModal = true"
                            :title="s__('ProjectSettings|Task Cloning Information')"
                            aria-label="Task Cloning Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{
                            s__(
                              'ProjectSettings|Allow members to create multiple copies of a task with different due dates'
                            )
                          }}
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectBoardsEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Boards') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showBoardsInfoModal = true"
                            :title="s__('ProjectSettings|Boards Information')"
                            aria-label="Boards Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{
                            s__(
                              'ProjectSettings|Allow members to create and use boards for visualizing tasks'
                            )
                          }}
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectChecklistsEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Checklists') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showChecklistsInfoModal = true"
                            :title="s__('ProjectSettings|Checklists Information')"
                            aria-label="Checklists Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{
                            s__(
                              'ProjectSettings|Allow members to create checklists for tasks to track completion requirements'
                            )
                          }}
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="settings-toggle-with-info">
                          <label class="checkbox-label">
                            <input
                              v-model="projectTemplatesEnabled"
                              type="checkbox"
                              :disabled="saving"
                            />
                            <span>{{ s__('ProjectSettings|Enable Task Templates') }}</span>
                          </label>
                          <button
                            type="button"
                            class="info-icon-button"
                            @click.stop="showTemplatesInfoModal = true"
                            :title="s__('ProjectSettings|Task Templates Information')"
                            aria-label="Task Templates Information"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                        <div class="help-text">
                          {{
                            s__(
                              'ProjectSettings|Allow members to create and use task templates for this project'
                            )
                          }}
                        </div>
                      </div>
                    </div>
                    <TelegramGroupSettings
                      :project="project"
                      :project-id="projectId"
                      :is-personal-project="isPersonalProject"
                      :saving="saving"
                      :group-chat-notifications-enabled="projectGroupChatNotificationsEnabled"
                      @update:group-chat-notifications-enabled="
                        projectGroupChatNotificationsEnabled = $event
                      "
                      @project-updated="refetchBase()"
                    />
                  </div>
                </div>

                <!-- Members (visible only if user can manage members and not personal project) -->
                <div
                  v-if="canManageMembers && !isPersonalProject"
                  v-show="activeSettingsSection === 'members'"
                  data-settings-section="members"
                >
                  <MembersList
                    :project-id="projectId"
                    :project="project"
                    :loading="membersLoading"
                    :memberships="memberships"
                    :alert="membersListAlert"
                    :expanded="expandedSections.members"
                    :telegram-status-expanded="telegramStatusExpanded"
                    :telegram-status-loading="isTelegramStatusLoading"
                    :telegram-status-loaded="isTelegramStatusLoaded"
                    :telegram-status-load-state="telegramStatusLoadState"
                    :on-refetch="refetch"
                    @update:expanded="expandedSections.members = $event"
                    @update:telegram-status-expanded="telegramStatusExpanded = $event"
                    @load-telegram-status="loadMembersTelegramStatus"
                    @refetch="refetch"
                    @remove-member-with-tasks="handleRemoveMemberWithTasks"
                    @show-alert="handleMembersListAlert"
                    @dismiss-alert="membersListAlert = null"
                  />
                </div>

                <!-- Invitations (visible only if user can manage members and not personal project) -->
                <div
                  v-if="canManageMembers && !isPersonalProject"
                  v-show="activeSettingsSection === 'invitations'"
                  class="settings-block"
                  data-settings-section="invitations"
                >
                  <div v-show="expandedSections.invitations" class="section-content">
                    <InvitationsList
                      :project-id="projectId"
                      :expanded="expandedSections.invitations"
                      @update:expanded="expandedSections.invitations = $event"
                    />
                  </div>
                </div>

                <!-- Required Fields (visible only if user can update project and not personal project) -->
                <div
                  v-if="canUpdateProject && !isPersonalProject"
                  v-show="activeSettingsSection === 'requiredFields'"
                  class="settings-block"
                  data-settings-section="requiredFields"
                >
                  <div v-show="expandedSections.requiredFields" class="section-content">
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Task Fields') }}</label>
                      <div class="help-text" style="margin-bottom: 12px">
                        {{
                          s__(
                            'ProjectSettings|Select which fields are required for tasks in this project'
                          )
                        }}
                      </div>
                      <div class="required-fields-list mobile-underlined-checklist">
                        <div class="required-fields-option-row mobile-underlined-checklist-item">
                          <label class="checkbox-label required-fields-option">
                            <input
                              v-model="projectRequiredFields"
                              type="checkbox"
                              value="priority"
                              :disabled="savingRequiredFields"
                            />
                            <span>{{ s__('ProjectSettings|Priority') }}</span>
                          </label>
                        </div>
                        <div class="required-fields-option-row mobile-underlined-checklist-item">
                          <label class="checkbox-label required-fields-option">
                            <input
                              v-model="projectRequiredFields"
                              type="checkbox"
                              value="assignee_id"
                              :disabled="savingRequiredFields"
                            />
                            <span>{{ s__('ProjectSettings|Assignee') }}</span>
                          </label>
                        </div>
                        <div class="required-fields-option-row mobile-underlined-checklist-item">
                          <label class="checkbox-label required-fields-option">
                            <input
                              v-model="projectRequiredFields"
                              type="checkbox"
                              value="due_at"
                              :disabled="savingRequiredFields"
                            />
                            <span>{{ s__('ProjectSettings|Due At') }}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <button
                      :disabled="savingRequiredFields || !hasRequiredFieldsChanges"
                      class="btn-primary mobile-stretch-btn"
                      style="margin-top: 12px"
                      @click="saveRequiredFields"
                    >
                      <span v-if="savingRequiredFields">{{
                        s__('ProjectSettings|Saving...')
                      }}</span>
                      <span v-else>{{ s__('ProjectSettings|Save') }}</span>
                    </button>
                    <div v-if="saveRequiredFieldsError" class="error" style="margin-top: 8px">
                      {{ saveRequiredFieldsError }}
                    </div>
                    <div v-if="saveRequiredFieldsSuccess" class="success" style="margin-top: 8px">
                      {{ s__('ProjectSettings|Settings saved successfully!') }}
                    </div>
                  </div>
                </div>

                <!-- Labels -->
                <div
                  v-show="activeSettingsSection === 'labels'"
                  class="settings-block"
                  data-settings-section="labels"
                >
                  <div v-show="expandedSections.labels" class="section-content">
                    <LabelsManager
                      ref="labelsManagerRef"
                      scope="project"
                      :project-id="projectId"
                      :expanded="expandedSections.labels"
                      :show-create-button="false"
                      :empty-message="s__('ProjectSettings|No labels defined yet.')"
                      :add-button-text="s__('ProjectSettings|Add Label')"
                      @refetch="refetchBase()"
                    />
                  </div>
                </div>

                <!-- Custom Report Configuration (visible only if user can update project and not personal project) -->
                <div
                  v-if="canUpdateProject && !isPersonalProject && project"
                  v-show="activeSettingsSection === 'customReport'"
                  class="settings-block"
                  data-settings-section="customReport"
                >
                  <div v-show="expandedSections.customReport" class="section-content">
                    <ReportConfigurationPanel
                      context="project"
                      :project-id="String(project.id)"
                      :title="''"
                      :is-personal-project="false"
                      :personal-labels-enabled="false"
                      :show-task-type-filter="true"
                      :hide-by-project-option="true"
                    />
                  </div>
                </div>

                <!-- Regular Reports (visible only if user can update project, not personal project, and has telegram chat) -->
                <div
                  v-if="canUpdateProject && !isPersonalProject && project?.telegramChatId"
                  v-show="activeSettingsSection === 'regularReports'"
                  class="settings-block"
                  data-settings-section="regularReports"
                >
                  <div v-show="expandedSections.regularReports" class="section-content">
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Regular Deadline Reports') }}</label>
                      <RegularReportsPanel
                        :hour="projectDeadlineReportHour"
                        :minute="projectDeadlineReportMinute"
                        :days="projectDeadlineReportDays"
                        :disabled="saving || savingReports || !hasProjectTimezone"
                        :saving="false"
                        :preferred-language="projectLanguage"
                        translation-prefix="ProjectSettings"
                        :time-label="s__('ProjectSettings|Time')"
                        :days-label="s__('ProjectSettings|Days')"
                        :help-text="''"
                        :show-save-button="false"
                        @update:hour="projectDeadlineReportHour = $event"
                        @update:minute="projectDeadlineReportMinute = $event"
                        @update:days="projectDeadlineReportDays = $event"
                        @clear="clearProjectDeadlineReport"
                      />
                      <div v-if="!hasProjectTimezone" class="help-text" style="color: #d97706">
                        {{
                          s__(
                            'ProjectSettings|Please set your preferred timezone above to configure deadline reports'
                          )
                        }}
                      </div>
                      <div v-else class="help-text">
                        {{
                          s__(
                            'ProjectSettings|Receive a daily deadline report at the specified time on selected days (times are in your preferred timezone)'
                          )
                        }}
                      </div>
                    </div>
                    <div class="form-row">
                      <label>{{ s__('ProjectSettings|Daily Completed Tasks Reports') }}</label>
                      <RegularReportsPanel
                        :hour="projectCompletedTasksReportHour"
                        :minute="projectCompletedTasksReportMinute"
                        :days="projectCompletedTasksReportDays"
                        :disabled="saving || savingReports || !hasProjectTimezone"
                        :saving="false"
                        :preferred-language="projectLanguage"
                        translation-prefix="ProjectSettings"
                        :time-label="s__('ProjectSettings|Time')"
                        :days-label="s__('ProjectSettings|Days')"
                        :help-text="''"
                        :show-save-button="false"
                        @update:hour="projectCompletedTasksReportHour = $event"
                        @update:minute="projectCompletedTasksReportMinute = $event"
                        @update:days="projectCompletedTasksReportDays = $event"
                        @clear="clearProjectCompletedTasksReport"
                      />
                      <div v-if="!hasProjectTimezone" class="help-text" style="color: #d97706">
                        {{
                          s__(
                            'ProjectSettings|Please set your preferred timezone above to configure completed tasks reports'
                          )
                        }}
                      </div>
                      <div v-else class="help-text">
                        {{
                          s__(
                            'ProjectSettings|Receive a daily report of tasks completed within the last 24 hours at the specified time on selected days (times are in your preferred timezone)'
                          )
                        }}
                      </div>
                    </div>
                    <button
                      :disabled="savingReports || !hasRegularReportsChanges"
                      class="btn-primary mobile-stretch-btn"
                      style="margin-top: 12px"
                      @click="saveRegularReports"
                    >
                      <span v-if="savingReports">{{ s__('ProjectSettings|Saving...') }}</span>
                      <span v-else>{{ s__('ProjectSettings|Save') }}</span>
                    </button>
                    <div v-if="saveReportsError" class="error" style="margin-top: 8px">
                      {{ saveReportsError }}
                    </div>
                    <div v-if="saveReportsSuccess" class="success" style="margin-top: 8px">
                      {{ s__('ProjectSettings|Settings saved successfully!') }}
                    </div>
                  </div>
                </div>

                <!-- Notification Settings (visible only if user can update project, not personal project, and has telegram chat) -->
                <div
                  v-if="canUpdateProject && !isPersonalProject && project?.telegramChatId"
                  v-show="activeSettingsSection === 'notifications'"
                  class="settings-block"
                  data-settings-section="notifications"
                >
                  <div v-show="expandedSections.notifications" class="section-content">
                    <div class="form-row">
                      <div style="display: flex; align-items: center; gap: 8px">
                        <label>{{ s__('ProjectSettings|Silent hours') }}</label>
                        <button
                          type="button"
                          class="info-icon-button"
                          @click.stop="showSilentHoursInfoModal = true"
                          :title="s__('ProjectSettings|Silent Hours Information')"
                          aria-label="Silent Hours Information"
                        >
                          <InfoIcon />
                        </button>
                      </div>
                      <SilentHoursForm
                        v-model:start-hour="projectSilentHoursStartHour"
                        v-model:start-minute="projectSilentHoursStartMinute"
                        v-model:end-hour="projectSilentHoursEndHour"
                        v-model:end-minute="projectSilentHoursEndMinute"
                        :inputs-disabled="saving || savingSilentHours || !hasProjectTimezone"
                        :clear-disabled="saving || savingSilentHours || !hasProjectSilentHours"
                        :save-disabled="
                          saving || savingSilentHours || !hasProjectSilentHoursChanges
                        "
                        :saving="savingSilentHours"
                        :start-label="s__('ProjectSettings|Start')"
                        :end-label="s__('ProjectSettings|End')"
                        :clear-text="__('Clear')"
                        :save-text="s__('ProjectSettings|Save')"
                        :saving-text="s__('ProjectSettings|Saving...')"
                        @clear="clearProjectSilentHours"
                        @save="saveSilentHours"
                      />
                      <AlertBanner
                        v-if="silentHoursAlert"
                        :type="silentHoursAlert.type"
                        :title="silentHoursAlert.title"
                        :message="silentHoursAlert.message"
                        @dismiss="silentHoursAlert = null"
                      />
                      <div v-if="!hasProjectTimezone" class="help-text" style="color: #d97706">
                        {{
                          s__(
                            'ProjectSettings|Please set your preferred timezone above to configure silent hours'
                          )
                        }}
                      </div>
                      <div v-else class="help-text">
                        {{
                          s__(
                            'ProjectSettings|Do not send group chat notifications during these hours (times are in your preferred timezone)'
                          )
                        }}
                      </div>
                    </div>
                    <NotificationSettings
                      v-if="expandedSections.notifications"
                      :project-id="projectId"
                      :can-update-project="canUpdateProject"
                      :group-chat-notifications-enabled="projectGroupChatNotificationsEnabled"
                    />
                  </div>
                </div>

                <!-- Task Templates -->
                <div
                  v-if="projectTemplatesEnabled"
                  v-show="activeSettingsSection === 'taskTemplates'"
                  class="settings-block"
                  data-settings-section="taskTemplates"
                >
                  <div v-show="expandedSections.taskTemplates" class="section-content">
                    <TaskTemplatesManager
                      ref="taskTemplatesManagerRef"
                      scope="project"
                      :project-id="projectId"
                      :expanded="expandedSections.taskTemplates"
                      :disabled="saving"
                      :show-create-button="false"
                      :empty-message="s__('ProjectSettings|No task templates defined yet.')"
                      :add-button-text="s__('ProjectSettings|New Template')"
                      @refetch="refetchBase()"
                    />
                  </div>
                </div>

                <div
                  v-show="activeSettingsSection === 'horizontalRelationTypes'"
                  class="settings-block"
                  data-settings-section="horizontalRelationTypes"
                >
                  <div v-show="expandedSections.horizontalRelationTypes" class="section-content">
                    <div class="help-text" style="margin-bottom: 12px">
                      {{
                        s__(
                          'ProjectSettings|Define how tasks can be linked to each other. Examples: "Blocks / Blocked by" for dependencies, "Relates to / Relates to" for general connections, "Causes / Is caused by" for causality.'
                        )
                      }}
                    </div>
                    <HorizontalRelationTypesList
                      ref="horizontalRelationTypesRef"
                      :project-id="projectId"
                      :expanded="expandedSections.horizontalRelationTypes"
                      :show-create-button="false"
                    />
                  </div>
                </div>

                <!-- Task Categories -->
                <div
                  v-show="activeSettingsSection === 'taskCategories'"
                  class="settings-block"
                  data-settings-section="taskCategories"
                >
                  <div v-show="expandedSections.taskCategories" class="section-content">
                    <div v-if="taskCategoriesLoading" class="muted" style="margin-bottom: 12px">
                      {{ __('Loading…') }}
                    </div>
                    <div
                      v-else-if="taskCategories.length === 0"
                      class="muted"
                      style="margin-bottom: 12px"
                    >
                      {{ s__('ProjectSettings|No task categories defined yet.') }}
                    </div>
                    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                      <li v-for="tt in taskCategories" :key="tt.id" class="task-category-item">
                        <div style="flex: 1">
                          <div>
                            <strong>{{ tt.name }}</strong>
                          </div>
                          <div class="muted" style="font-size: 13px; margin-top: 2px">
                            {{ s__('ProjectSettings|Workflow:') }}
                            {{ tt.workflow?.name || s__('ProjectSettings|None') }}
                            <span v-if="gradesEnabled && tt.gradingScale" style="margin-left: 12px">
                              {{ s__('ProjectSettings|Grading Scale:') }} {{ tt.gradingScale.name }}
                            </span>
                          </div>
                        </div>
                        <div style="display: flex; gap: 8px">
                          <button class="btn-sm" @click="editTaskCategory(tt)">
                            <span class="btn-text">{{ s__('ProjectSettings|Edit') }}</span>
                            <span><EditIcon /></span>
                          </button>
                          <button class="btn-sm btn-danger" @click="deleteTaskCategory(tt)">
                            <span class="btn-text">{{ s__('ProjectSettings|Delete') }}</span>
                            <span><TrashIcon /></span>
                          </button>
                        </div>
                      </li>
                    </ul>
                    <!-- New Task Category Form -->
                    <div v-if="showNewTaskCategoryForm" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Add Task Category') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelNewTaskCategory"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="task-category-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label class="single-line-label">{{
                                  s__('ProjectSettings|Name')
                                }}</label>
                                <input
                                  v-model="newTaskCategory.name"
                                  type="text"
                                  :placeholder="s__('ProjectSettings|e.g., Bug, Feature, Task')"
                                />
                              </div>
                              <div style="flex: 1">
                                <label class="single-line-label">{{
                                  s__('ProjectSettings|Workflow')
                                }}</label>
                                <select v-model="newTaskCategory.workflowId">
                                  <option :value="undefined">
                                    {{ s__('ProjectSettings|Auto-select default') }}
                                  </option>
                                  <option v-for="wf in workflows" :key="wf.id" :value="wf.id">
                                    {{ wf.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div v-if="gradesEnabled" class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Grading Scale') }}</label>
                                <select v-model="newTaskCategory.gradingScaleId">
                                  <option :value="null">{{ s__('ProjectSettings|None') }}</option>
                                  <option v-for="gs in gradingScales" :key="gs.id" :value="gs.id">
                                    {{ gs.name }}
                                  </option>
                                </select>
                                <div class="help-text">
                                  {{
                                    s__(
                                      'ProjectSettings|Optional: Select a grading scale for this task category'
                                    )
                                  }}
                                </div>
                              </div>
                            </div>
                            <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                              <button
                                :disabled="!canCreateTaskCategory || submittingTaskCategory"
                                class="btn-primary"
                                @click="createTaskCategory"
                              >
                                {{
                                  submittingTaskCategory
                                    ? s__('ProjectSettings|Creating...')
                                    : s__('ProjectSettings|Create')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelNewTaskCategory">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="taskCategoryError" class="error" style="margin-top: 8px">
                              {{ taskCategoryError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Edit Task Category Form -->
                    <div v-if="editingTaskCategory" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Edit Task Category') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelEditTaskCategory"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="task-category-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label class="single-line-label">{{
                                  s__('ProjectSettings|Name')
                                }}</label>
                                <input
                                  v-model="editingTaskCategory.name"
                                  type="text"
                                  :placeholder="s__('ProjectSettings|e.g., Bug, Feature, Task')"
                                />
                              </div>
                              <div style="flex: 1">
                                <label class="single-line-label">{{
                                  s__('ProjectSettings|Workflow')
                                }}</label>
                                <select v-model="editingTaskCategory.workflowId">
                                  <option v-for="wf in workflows" :key="wf.id" :value="wf.id">
                                    {{ wf.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div v-if="gradesEnabled" class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Grading Scale') }}</label>
                                <select v-model="editingTaskCategory.gradingScaleId">
                                  <option :value="null">{{ s__('ProjectSettings|None') }}</option>
                                  <option v-for="gs in gradingScales" :key="gs.id" :value="gs.id">
                                    {{ gs.name }}
                                  </option>
                                </select>
                                <div class="help-text">
                                  {{
                                    s__(
                                      'ProjectSettings|Optional: Select a grading scale for this task category'
                                    )
                                  }}
                                </div>
                              </div>
                            </div>
                            <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                              <button
                                :disabled="!canUpdateTaskCategory || submittingTaskCategory"
                                class="btn-primary"
                                @click="updateTaskCategory"
                              >
                                {{
                                  submittingTaskCategory
                                    ? s__('ProjectSettings|Saving...')
                                    : s__('ProjectSettings|Save')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelEditTaskCategory">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="taskCategoryError" class="error" style="margin-top: 8px">
                              {{ taskCategoryError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Workflows -->
                <div
                  v-show="activeSettingsSection === 'workflows'"
                  class="settings-block"
                  data-settings-section="workflows"
                >
                  <div v-show="expandedSections.workflows" class="section-content">
                    <div v-if="workflowsLoading" class="muted" style="margin-bottom: 12px">
                      {{ __('Loading…') }}
                    </div>
                    <div
                      v-else-if="workflows.length === 0"
                      class="muted"
                      style="margin-bottom: 12px"
                    >
                      {{ s__('ProjectSettings|No workflows defined yet.') }}
                    </div>
                    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                      <li v-for="wf in workflows" :key="wf.id" class="workflow-item">
                        <div class="workflow-item-main">
                          <div class="workflow-title-row">
                            <strong class="workflow-title">{{ wf.name }}</strong>
                            <span v-if="wf.isDefault" class="badge-default">{{
                              s__('ProjectSettings|Default')
                            }}</span>
                          </div>
                          <div
                            v-if="wf.description"
                            class="muted"
                            style="font-size: 13px; margin-top: 4px"
                          >
                            {{ wf.description }}
                          </div>
                          <div class="workflow-stats">
                            {{
                              n__('%d status', '%d statuses', wf.statuses.length).replace(
                                '%d',
                                wf.statuses.length
                              )
                            }},
                            {{
                              n__('%d transition', '%d transitions', wf.transitions.length).replace(
                                '%d',
                                wf.transitions.length
                              )
                            }}
                          </div>
                        </div>
                        <div class="workflow-item-actions">
                          <button class="btn-sm" @click="viewWorkflow(wf)">
                            <span class="btn-text">{{ s__('ProjectSettings|View') }}</span>
                            <span><EyeIcon color="currentColor" /></span>
                          </button>
                          <button class="btn-sm" @click="editWorkflow(wf)">
                            <span class="btn-text">{{ s__('ProjectSettings|Edit') }}</span>
                            <span><EditIcon /></span>
                          </button>
                          <button
                            v-if="!wf.isDefault"
                            class="btn-sm btn-danger"
                            @click="deleteWorkflow(wf)"
                          >
                            <span class="btn-text">{{ s__('ProjectSettings|Delete') }}</span>
                            <span><TrashIcon /></span>
                          </button>
                        </div>
                      </li>
                    </ul>
                    <!-- New Workflow Form -->
                    <div v-if="showNewWorkflowForm" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Add Workflow') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelNewWorkflow"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="workflow-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Name') }} *</label>
                                <input
                                  v-model="newWorkflow.name"
                                  type="text"
                                  :placeholder="
                                    s__('ProjectSettings|e.g., Bug Tracking, Development')
                                  "
                                />
                              </div>
                            </div>
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Description') }}</label>
                                <textarea
                                  v-model="newWorkflow.description"
                                  :placeholder="s__('ProjectSettings|Optional description')"
                                  rows="2"
                                ></textarea>
                              </div>
                            </div>
                            <div class="row">
                              <label style="display: flex; align-items: center; gap: 8px">
                                <input v-model="newWorkflow.isDefault" type="checkbox" />
                                <span>{{ s__('ProjectSettings|Set as default workflow') }}</span>
                              </label>
                            </div>
                            <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                              <button
                                :disabled="!canCreateWorkflow || submittingWorkflow"
                                class="btn-primary"
                                @click="createWorkflow"
                              >
                                {{
                                  submittingWorkflow
                                    ? s__('ProjectSettings|Creating...')
                                    : s__('ProjectSettings|Create')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelNewWorkflow">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="workflowError" class="error" style="margin-top: 8px">
                              {{ workflowError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Edit Workflow Form -->
                    <div v-if="editingWorkflow" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Edit Workflow') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelEditWorkflow"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="workflow-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Name') }} *</label>
                                <input
                                  v-model="editingWorkflow.name"
                                  type="text"
                                  :placeholder="
                                    s__('ProjectSettings|e.g., Bug Tracking, Development')
                                  "
                                />
                              </div>
                            </div>
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Description') }}</label>
                                <textarea
                                  v-model="editingWorkflow.description"
                                  :placeholder="s__('ProjectSettings|Optional description')"
                                  rows="2"
                                ></textarea>
                              </div>
                            </div>
                            <div class="row">
                              <label style="display: flex; align-items: center; gap: 8px">
                                <input v-model="editingWorkflow.isDefault" type="checkbox" />
                                <span>{{ s__('ProjectSettings|Set as default workflow') }}</span>
                              </label>
                            </div>
                            <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                              <button
                                :disabled="!canUpdateWorkflow || submittingWorkflow"
                                class="btn-primary"
                                @click="updateWorkflow"
                              >
                                {{
                                  submittingWorkflow
                                    ? s__('ProjectSettings|Saving...')
                                    : s__('ProjectSettings|Save')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelEditWorkflow">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="workflowError" class="error" style="margin-top: 8px">
                              {{ workflowError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- View/Manage Workflow Details -->
                    <div v-if="viewingWorkflow" class="modal-overlay">
                      <div class="modal-content settings-modal-content settings-modal-content-wide">
                        <div class="modal-body">
                          <div class="workflow-details">
                            <div
                              style="
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                margin-bottom: 16px;
                              "
                            >
                              <h4 style="margin: 0">{{ viewingWorkflow.name }}</h4>
                              <button class="btn-sm btn-secondary" @click="closeViewWorkflow">
                                {{ s__('ProjectSettings|Close') }}
                              </button>
                            </div>

                            <!-- Task Types using this workflow -->
                            <div
                              v-if="taskCategoriesForWorkflow(viewingWorkflow.id).length > 0"
                              class="workflow-section"
                            >
                              <h5 style="margin: 0 0 12px 0">
                                {{ s__('ProjectSettings|Task Categories') }}
                              </h5>
                              <div class="muted" style="font-size: 13px; margin-bottom: 8px">
                                {{ s__('ProjectSettings|Task categories using this workflow:') }}
                              </div>
                              <div style="display: flex; flex-wrap: wrap; gap: 8px">
                                <span
                                  v-for="tt in taskCategoriesForWorkflow(viewingWorkflow.id)"
                                  :key="tt.id"
                                  class="task-category-badge"
                                >
                                  {{ tt.name }}
                                </span>
                              </div>
                            </div>

                            <!-- Statuses Section -->
                            <div class="workflow-section">
                              <h5 style="margin: 0 0 12px 0">
                                {{ s__('ProjectSettings|Statuses') }}
                              </h5>
                              <div
                                v-if="viewingWorkflow.statuses.length === 0"
                                class="muted"
                                style="margin-bottom: 12px"
                              >
                                {{ s__('ProjectSettings|No statuses defined yet.') }}
                              </div>
                              <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                                <li
                                  v-for="status in sortedStatuses"
                                  :key="status.id"
                                  class="status-item"
                                >
                                  <div class="status-item-main">
                                    <div class="status-title-row">
                                      <strong class="status-title">{{ status.name }}</strong>
                                      <div class="status-badges-row">
                                        <span v-if="status.isInitial" class="badge-initial">{{
                                          s__('ProjectSettings|Initial')
                                        }}</span>
                                        <span v-if="status.isFinal" class="badge-final">{{
                                          s__('ProjectSettings|Final')
                                        }}</span>
                                        <span
                                          v-if="status.requiresCompletedChecklists"
                                          class="badge-checklist"
                                          :title="
                                            s__('ProjectSettings|Requires completed checklists')
                                          "
                                        >
                                          ✓ {{ s__('ProjectSettings|Checklist') }}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      v-if="status.description"
                                      class="muted"
                                      style="font-size: 12px; margin-top: 2px"
                                    >
                                      {{ status.description }}
                                    </div>
                                  </div>
                                  <div class="status-item-actions">
                                    <button class="btn-sm" @click="editStatus(status)">
                                      <span class="btn-text">{{
                                        s__('ProjectSettings|Edit')
                                      }}</span>
                                      <span><EditIcon /></span>
                                    </button>
                                    <button class="btn-sm btn-danger" @click="deleteStatus(status)">
                                      <span class="btn-text">{{
                                        s__('ProjectSettings|Delete')
                                      }}</span>
                                      <span><TrashIcon /></span>
                                    </button>
                                  </div>
                                </li>
                              </ul>
                              <button
                                v-if="!showNewStatusForm"
                                class="btn-primary mobile-stretch-btn"
                                @click="showNewStatusForm = true"
                              >
                                {{ s__('ProjectSettings|Add Status') }}
                              </button>

                              <!-- New Status Form -->
                              <WorkflowStatusForm
                                v-if="showNewStatusForm"
                                class="status-form"
                                :status="newStatus"
                                :can-submit="canCreateStatus"
                                :submitting="submittingStatus"
                                :submit-text="s__('ProjectSettings|Create')"
                                :submitting-text="s__('ProjectSettings|Creating...')"
                                :error="statusError"
                                @update:status="newStatus = $event"
                                @submit="createStatus"
                                @cancel="cancelNewStatus"
                              />

                              <!-- Edit Status Form -->
                              <WorkflowStatusForm
                                v-if="editingStatus"
                                class="status-form"
                                :status="editingStatus"
                                :title="s__('ProjectSettings|Edit Status')"
                                :can-submit="canUpdateStatus"
                                :submitting="submittingStatus"
                                :submit-text="s__('ProjectSettings|Save')"
                                :submitting-text="s__('ProjectSettings|Saving...')"
                                :error="statusError"
                                @update:status="editingStatus = $event"
                                @submit="updateStatus"
                                @cancel="cancelEditStatus"
                              />
                            </div>

                            <!-- Transitions Section -->
                            <div class="workflow-section">
                              <h5 style="margin: 0 0 12px 0">
                                {{ s__('ProjectSettings|Transitions') }}
                              </h5>
                              <div
                                v-if="viewingWorkflow.transitions.length === 0"
                                class="muted"
                                style="margin-bottom: 12px"
                              >
                                {{ s__('ProjectSettings|No transitions defined yet.') }}
                              </div>
                              <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                                <li
                                  v-for="transition in viewingWorkflow.transitions"
                                  :key="transition.id"
                                  class="transition-item"
                                >
                                  <div class="transition-item-main">
                                    <div v-if="transition.isGlobal">
                                      <strong>{{ s__('ProjectSettings|Any status') }}</strong>
                                      <span class="transition-target-line">
                                        <span class="transition-arrow">→</span>
                                        <strong>{{ transition.targetStatus.name }}</strong>
                                      </span>
                                      <span class="badge-global">{{
                                        s__('ProjectSettings|Global')
                                      }}</span>
                                    </div>
                                    <div v-else>
                                      <strong v-if="transition.sourceStatuses.length === 1">{{
                                        transition.sourceStatuses[0].name
                                      }}</strong>
                                      <strong v-else-if="transition.sourceStatuses.length > 1">
                                        {{ transition.sourceStatuses.map(s => s.name).join(', ') }}
                                      </strong>
                                      <strong v-else class="muted">{{
                                        s__('ProjectSettings|No sources')
                                      }}</strong>
                                      <span class="transition-target-line">
                                        <span class="transition-arrow">→</span>
                                        <strong>{{ transition.targetStatus.name }}</strong>
                                      </span>
                                    </div>
                                    <div v-if="transition.name" class="transition-name-primary">
                                      {{ transition.name }}
                                    </div>
                                  </div>
                                  <div class="transition-item-actions">
                                    <button
                                      class="btn-sm btn-danger"
                                      @click="deleteTransition(transition)"
                                    >
                                      <span class="btn-text">{{
                                        s__('ProjectSettings|Delete')
                                      }}</span>
                                      <span><TrashIcon /></span>
                                    </button>
                                  </div>
                                </li>
                              </ul>
                              <button
                                v-if="
                                  !showNewTransitionForm && viewingWorkflow.statuses.length >= 2
                                "
                                class="btn-primary mobile-stretch-btn"
                                @click="showNewTransitionForm = true"
                              >
                                {{ s__('ProjectSettings|Add Transition') }}
                              </button>
                              <div
                                v-if="viewingWorkflow.statuses.length < 2"
                                class="muted"
                                style="font-size: 13px"
                              >
                                {{
                                  s__(
                                    'ProjectSettings|Add at least 2 statuses to create transitions.'
                                  )
                                }}
                              </div>

                              <!-- New Transition Form -->
                              <div v-if="showNewTransitionForm" class="transition-form">
                                <div class="row">
                                  <label style="display: flex; align-items: center; gap: 8px">
                                    <input v-model="newTransition.isGlobal" type="checkbox" />
                                    <span>{{
                                      s__('ProjectSettings|Global transition (from any status)')
                                    }}</span>
                                  </label>
                                </div>
                                <div v-if="!newTransition.isGlobal" class="row">
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|From Status') }} *</label>
                                    <select
                                      v-model="newTransition.sourceStatusIds"
                                      multiple
                                      size="4"
                                    >
                                      <option
                                        v-for="s in viewingWorkflow.statuses"
                                        :key="s.id"
                                        :value="s.id"
                                      >
                                        {{ s.name }}
                                      </option>
                                    </select>
                                    <div class="muted" style="font-size: 12px; margin-top: 4px">
                                      {{ s__('ProjectSettings|Hold Ctrl/Cmd to select multiple') }}
                                    </div>
                                  </div>
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|To Status') }} *</label>
                                    <select v-model="newTransition.targetStatusId">
                                      <option value="">
                                        {{ s__('ProjectSettings|Select target status') }}
                                      </option>
                                      <option
                                        v-for="s in viewingWorkflow.statuses"
                                        :key="s.id"
                                        :value="s.id"
                                      >
                                        {{ s.name }}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                                <div v-else class="row">
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|To Status') }} *</label>
                                    <select v-model="newTransition.targetStatusId">
                                      <option value="">
                                        {{ s__('ProjectSettings|Select target status') }}
                                      </option>
                                      <option
                                        v-for="s in viewingWorkflow.statuses"
                                        :key="s.id"
                                        :value="s.id"
                                      >
                                        {{ s.name }}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                                <div class="row">
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|Name (optional)') }}</label>
                                    <input
                                      v-model="newTransition.name"
                                      type="text"
                                      :placeholder="
                                        s__('ProjectSettings|e.g., Start Work, Complete')
                                      "
                                    />
                                  </div>
                                </div>
                                <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                                  <button
                                    :disabled="!canCreateTransition || submittingTransition"
                                    class="btn-primary"
                                    @click="createTransition"
                                  >
                                    {{
                                      submittingTransition
                                        ? s__('ProjectSettings|Creating...')
                                        : s__('ProjectSettings|Create')
                                    }}
                                  </button>
                                  <button class="btn-secondary" @click="cancelNewTransition">
                                    {{ __('Cancel') }}
                                  </button>
                                </div>
                                <div v-if="transitionError" class="error" style="margin-top: 8px">
                                  {{ transitionError }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Grading Scales (visible only if grades are enabled and not personal project) -->
                <div
                  v-if="gradesEnabled && !isPersonalProject"
                  v-show="activeSettingsSection === 'gradingScales'"
                  class="settings-block"
                  data-settings-section="gradingScales"
                >
                  <div v-show="expandedSections.gradingScales" class="section-content">
                    <div v-if="gradingScalesLoading" class="muted" style="margin-bottom: 12px">
                      {{ __('Loading…') }}
                    </div>
                    <div
                      v-else-if="gradingScales.length === 0"
                      class="muted"
                      style="margin-bottom: 12px"
                    >
                      {{ s__('ProjectSettings|No grading scales defined yet.') }}
                    </div>
                    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                      <li v-for="gs in gradingScales" :key="gs.id" class="grading-scale-item">
                        <div style="flex: 1">
                          <div style="display: flex; align-items: center; gap: 8px">
                            <strong>{{ gs.name }}</strong>
                          </div>
                          <div class="grading-scale-stats">
                            {{
                              n__('%d grade', '%d grades', gs.grades?.length || 0).replace(
                                '%d',
                                gs.grades?.length || 0
                              )
                            }}
                          </div>
                        </div>
                        <div style="display: flex; gap: 8px">
                          <button class="btn-sm" @click="viewGradingScale(gs)">
                            <span class="btn-text">{{ s__('ProjectSettings|View') }}</span>
                            <span><EyeIcon color="currentColor" /></span>
                          </button>
                          <button class="btn-sm" @click="editGradingScale(gs)">
                            <span class="btn-text">{{ s__('ProjectSettings|Edit') }}</span>
                            <span><EditIcon /></span>
                          </button>
                          <button class="btn-sm btn-danger" @click="deleteGradingScale(gs)">
                            <span class="btn-text">{{ s__('ProjectSettings|Delete') }}</span>
                            <span><TrashIcon /></span>
                          </button>
                        </div>
                      </li>
                    </ul>
                    <!-- New Grading Scale Form -->
                    <div v-if="showNewGradingScaleForm" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Add Grading Scale') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelNewGradingScale"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="grading-scale-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Name') }} *</label>
                                <input
                                  v-model="newGradingScale.name"
                                  type="text"
                                  :placeholder="
                                    s__('ProjectSettings|e.g., Letter Grades, Numeric Scale')
                                  "
                                />
                              </div>
                            </div>
                            <div class="mobile-stretch-btn-group" style="margin-top: 12px">
                              <button
                                :disabled="!canCreateGradingScale || submittingGradingScale"
                                class="btn-primary"
                                @click="createGradingScale"
                              >
                                {{
                                  submittingGradingScale
                                    ? s__('ProjectSettings|Creating...')
                                    : s__('ProjectSettings|Create')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelNewGradingScale">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="gradingScaleError" class="error" style="margin-top: 8px">
                              {{ gradingScaleError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Edit Grading Scale Form -->
                    <div v-if="editingGradingScale" class="modal-overlay">
                      <div class="modal-content settings-modal-content">
                        <div class="modal-header">
                          <h3>{{ s__('ProjectSettings|Edit Grading Scale') }}</h3>
                          <button
                            type="button"
                            class="modal-close"
                            aria-label="Close"
                            @click="cancelEditGradingScale"
                          >
                            ×
                          </button>
                        </div>
                        <div class="modal-body">
                          <div class="grading-scale-form">
                            <div class="row">
                              <div style="flex: 1">
                                <label>{{ s__('ProjectSettings|Name') }} *</label>
                                <input
                                  v-model="editingGradingScale.name"
                                  type="text"
                                  :placeholder="
                                    s__('ProjectSettings|e.g., Letter Grades, Numeric Scale')
                                  "
                                />
                              </div>
                            </div>
                            <div
                              class="mobile-stretch-btn-group"
                              style="display: flex; gap: 8px; margin-top: 12px"
                            >
                              <button
                                :disabled="!canUpdateGradingScale || submittingGradingScale"
                                class="btn-primary"
                                @click="updateGradingScale"
                              >
                                {{
                                  submittingGradingScale
                                    ? s__('ProjectSettings|Saving...')
                                    : s__('ProjectSettings|Save')
                                }}
                              </button>
                              <button class="btn-secondary" @click="cancelEditGradingScale">
                                {{ __('Cancel') }}
                              </button>
                            </div>
                            <div v-if="gradingScaleError" class="error" style="margin-top: 8px">
                              {{ gradingScaleError }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- View/Manage Grading Scale Details -->
                    <div v-if="viewingGradingScale" class="modal-overlay">
                      <div class="modal-content settings-modal-content settings-modal-content-wide">
                        <div class="modal-body">
                          <div class="grading-scale-details">
                            <div
                              style="
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                margin-bottom: 16px;
                              "
                            >
                              <h4 style="margin: 0">{{ viewingGradingScale.name }}</h4>
                              <button class="btn-sm btn-secondary" @click="closeViewGradingScale">
                                {{ s__('ProjectSettings|Close') }}
                              </button>
                            </div>

                            <!-- Grades Section -->
                            <div class="grading-scale-section">
                              <h5 style="margin: 0 0 12px 0">
                                {{ s__('ProjectSettings|Grades') }}
                              </h5>
                              <div class="muted" style="font-size: 13px; margin-bottom: 12px">
                                {{
                                  s__(
                                    'ProjectSettings|Grades are ordered by position (0 = highest/best grade).'
                                  )
                                }}
                              </div>
                              <div
                                v-if="sortedGrades.length === 0"
                                class="muted"
                                style="margin-bottom: 12px"
                              >
                                {{ s__('ProjectSettings|No grades defined yet.') }}
                              </div>
                              <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
                                <li
                                  v-for="grade in sortedGrades"
                                  :key="grade.id"
                                  class="grade-item"
                                >
                                  <div style="flex: 1">
                                    <div style="display: flex; align-items: center; gap: 8px">
                                      <strong>{{ grade.value }}</strong>
                                      <span
                                        v-if="grade.description"
                                        class="muted"
                                        style="font-size: 13px"
                                      >
                                        - {{ grade.description }}
                                      </span>
                                    </div>
                                    <div class="muted" style="font-size: 12px; margin-top: 2px">
                                      {{ s__('ProjectSettings|Position:') }} {{ grade.position }}
                                    </div>
                                  </div>
                                  <div style="display: flex; gap: 8px; align-items: center">
                                    <button class="btn-sm" @click="editGrade(grade)">
                                      <span class="btn-text">{{
                                        s__('ProjectSettings|Edit')
                                      }}</span>
                                      <span><EditIcon /></span>
                                    </button>
                                    <button class="btn-sm btn-danger" @click="deleteGrade(grade)">
                                      <span class="btn-text">{{
                                        s__('ProjectSettings|Delete')
                                      }}</span>
                                      <span><TrashIcon /></span>
                                    </button>
                                  </div>
                                </li>
                              </ul>
                              <button
                                v-if="!showNewGradeForm"
                                class="btn-sm"
                                @click="showNewGradeForm = true"
                              >
                                {{ s__('ProjectSettings|Add Grade') }}
                              </button>

                              <!-- New Grade Form -->
                              <div v-if="showNewGradeForm" class="grade-form">
                                <div class="row">
                                  <div style="flex: 0 0 100px">
                                    <label>{{ s__('ProjectSettings|Value') }} *</label>
                                    <input
                                      v-model="newGrade.value"
                                      type="text"
                                      :placeholder="s__('ProjectSettings|e.g., A+, B, 5')"
                                      maxlength="3"
                                    />
                                  </div>
                                </div>
                                <div class="row">
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|Description') }}</label>
                                    <input
                                      v-model="newGrade.description"
                                      type="text"
                                      :placeholder="s__('ProjectSettings|Optional')"
                                      maxlength="32"
                                    />
                                  </div>
                                </div>
                                <div
                                  class="mobile-stretch-btn-group"
                                  style="display: flex; gap: 8px; margin-top: 12px"
                                >
                                  <button
                                    :disabled="!canCreateGrade || submittingGrade"
                                    class="btn-primary"
                                    @click="createGrade"
                                  >
                                    {{
                                      submittingGrade
                                        ? s__('ProjectSettings|Creating...')
                                        : s__('ProjectSettings|Create')
                                    }}
                                  </button>
                                  <button class="btn-secondary" @click="cancelNewGrade">
                                    {{ __('Cancel') }}
                                  </button>
                                </div>
                                <div v-if="gradeError" class="error" style="margin-top: 8px">
                                  {{ gradeError }}
                                </div>
                              </div>

                              <!-- Edit Grade Form -->
                              <div v-if="editingGrade" class="grade-form">
                                <h6 style="margin: 0 0 12px 0">
                                  {{ s__('ProjectSettings|Edit Grade') }}
                                </h6>
                                <div class="row">
                                  <div style="flex: 0 0 100px">
                                    <label>{{ s__('ProjectSettings|Value') }} *</label>
                                    <input
                                      v-model="editingGrade.value"
                                      type="text"
                                      :placeholder="s__('ProjectSettings|e.g., A+, B, 5')"
                                      maxlength="3"
                                    />
                                  </div>
                                </div>
                                <div class="row">
                                  <div style="flex: 1">
                                    <label>{{ s__('ProjectSettings|Description') }}</label>
                                    <input
                                      v-model="editingGrade.description"
                                      type="text"
                                      :placeholder="s__('ProjectSettings|Optional')"
                                      maxlength="32"
                                    />
                                  </div>
                                </div>
                                <div
                                  class="mobile-stretch-btn-group"
                                  style="display: flex; gap: 8px; margin-top: 12px"
                                >
                                  <button
                                    :disabled="!canUpdateGrade || submittingGrade"
                                    class="btn-primary"
                                    @click="updateGrade"
                                  >
                                    {{
                                      submittingGrade
                                        ? s__('ProjectSettings|Saving...')
                                        : s__('ProjectSettings|Save')
                                    }}
                                  </button>
                                  <button class="btn-secondary" @click="cancelEditGrade">
                                    {{ __('Cancel') }}
                                  </button>
                                </div>
                                <div v-if="gradeError" class="error" style="margin-top: 8px">
                                  {{ gradeError }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ownership Transfer (visible only if user is owner and not personal project) -->
                <div
                  v-if="isOwner && !isPersonalProject"
                  v-show="activeSettingsSection === 'ownershipTransfer'"
                  class="settings-block"
                  data-settings-section="ownershipTransfer"
                >
                  <div v-show="expandedSections.ownershipTransfer" class="section-content">
                    <OwnershipTransfer
                      :project-id="projectId"
                      :expanded="expandedSections.ownershipTransfer"
                      @update:expanded="expandedSections.ownershipTransfer = $event"
                    />
                  </div>
                </div>

                <!-- Archive Project -->
                <div
                  v-show="activeSettingsSection === 'archiveProject'"
                  class="settings-block"
                  data-settings-section="archiveProject"
                >
                  <ArchiveProject
                    :project-id="projectId"
                    :can-archive-project="canArchiveProject"
                    :is-personal-project="isPersonalProject"
                    :project-archived="projectArchived"
                    @project-archived="handleProjectArchived"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <div v-else class="error">{{ s__('ProjectSettings|Project not found.') }}</div>
    </div>

    <!-- Grade Transition Modal -->
    <Teleport to="body">
      <div v-if="showGradeTransitionModal" class="modal-overlay">
        <div class="modal-content" style="max-width: 600px">
          <div class="modal-header">
            <h3>{{ s__('ProjectSettings|Map Grades') }}</h3>
            <button class="modal-close" aria-label="Close" @click="closeGradeTransitionModal">
              ×
            </button>
          </div>
          <div class="modal-body">
            <p class="muted" style="margin-bottom: 16px">
              {{
                s__(
                  'ProjectSettings|Map each grade from the old grading scale to a grade in the new grading scale.'
                )
              }}
            </p>
            <div v-if="transitionOldScale && transitionNewScale">
              <div style="margin-bottom: 16px">
                <strong>{{ s__('ProjectSettings|From:') }}</strong> {{ transitionOldScale.name }}
                <span style="margin-left: 12px">
                  <strong>{{ s__('ProjectSettings|To:') }}</strong> {{ transitionNewScale.name }}
                </span>
              </div>
              <div style="display: flex; flex-direction: column; gap: 12px">
                <div
                  v-for="oldGrade in sortedOldGrades"
                  :key="oldGrade.id"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px;
                    background: #f5f5f5;
                    border-radius: 4px;
                  "
                >
                  <div style="flex: 1">
                    <strong>{{ oldGrade.value }}</strong>
                    <span v-if="oldGrade.description" class="muted" style="margin-left: 8px"
                      >- {{ oldGrade.description }}</span
                    >
                  </div>
                  <span style="margin: 0 8px">→</span>
                  <select
                    :value="gradeMappings[oldGrade.id] || null"
                    style="flex: 1; padding: 6px; border: 1px solid #ddd; border-radius: 4px"
                    required
                    @change="
                      gradeMappings[oldGrade.id] = $event.target.value
                        ? parseInt($event.target.value)
                        : null
                    "
                  >
                    <option
                      v-for="newGrade in sortedNewGrades"
                      :key="newGrade.id"
                      :value="newGrade.id.toString()"
                    >
                      {{ newGrade.value
                      }}{{ newGrade.description ? ' - ' + newGrade.description : '' }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end">
              <button class="btn-secondary" @click="closeGradeTransitionModal">
                {{ __('Cancel') }}
              </button>
              <button class="btn-primary" @click="confirmGradeTransition">
                {{ s__('ProjectSettings|Confirm') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Workflow Status Transition Modal -->
    <Teleport to="body">
      <div v-if="showWorkflowStatusTransitionModal" class="modal-overlay">
        <div class="modal-content" style="max-width: 600px">
          <div class="modal-header">
            <h3>{{ s__('ProjectSettings|Map Workflow Statuses') }}</h3>
            <button
              class="modal-close"
              aria-label="Close"
              @click="closeWorkflowStatusTransitionModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <p class="muted" style="margin-bottom: 16px">
              {{
                s__(
                  'ProjectSettings|Map each workflow status from the old workflow to a status in the new workflow.'
                )
              }}
            </p>
            <div v-if="transitionOldWorkflow && transitionNewWorkflow">
              <div style="margin-bottom: 16px">
                <strong>{{ s__('ProjectSettings|From:') }}</strong> {{ transitionOldWorkflow.name }}
                <span style="margin-left: 12px">
                  <strong>{{ s__('ProjectSettings|To:') }}</strong> {{ transitionNewWorkflow.name }}
                </span>
              </div>
              <div style="display: flex; flex-direction: column; gap: 12px">
                <div
                  v-for="oldStatus in sortedOldStatuses"
                  :key="oldStatus.id"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px;
                    background: #f5f5f5;
                    border-radius: 4px;
                  "
                >
                  <div style="flex: 1">
                    <strong>{{ oldStatus.name }}</strong>
                    <span v-if="oldStatus.description" class="muted" style="margin-left: 8px"
                      >- {{ oldStatus.description }}</span
                    >
                  </div>
                  <span style="margin: 0 8px">→</span>
                  <select
                    :value="workflowStatusMappings[oldStatus.id] || null"
                    style="flex: 1; padding: 6px; border: 1px solid #ddd; border-radius: 4px"
                    required
                    @change="
                      workflowStatusMappings[oldStatus.id] = $event.target.value
                        ? parseInt($event.target.value)
                        : null
                    "
                  >
                    <option
                      v-for="newStatus in sortedNewStatuses"
                      :key="newStatus.id"
                      :value="newStatus.id.toString()"
                    >
                      {{ newStatus.name
                      }}{{ newStatus.description ? ' - ' + newStatus.description : '' }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end">
              <button class="btn-secondary" @click="closeWorkflowStatusTransitionModal">
                {{ __('Cancel') }}
              </button>
              <button class="btn-primary" @click="confirmWorkflowStatusTransition">
                {{ s__('ProjectSettings|Confirm') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Status Deletion Transition Modal -->
    <Teleport to="body">
      <div v-if="showStatusDeletionTransitionModal" class="modal-overlay">
        <div class="modal-content" style="max-width: 500px">
          <div class="modal-header">
            <h3>{{ s__('ProjectSettings|Transition Tasks') }}</h3>
            <button
              class="modal-close"
              aria-label="Close"
              @click="closeStatusDeletionTransitionModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <p class="muted" style="margin-bottom: 16px">
              {{
                sprintf(
                  s__(
                    'ProjectSettings|The status "%{status}" has tasks assigned to it. Please select a status to transition these tasks to.'
                  ),
                  { status: deletingStatus?.name || '' }
                )
              }}
            </p>
            <div v-if="deletingStatus && viewingWorkflow">
              <label style="display: block; margin-bottom: 8px; font-weight: 500">
                {{ s__('ProjectSettings|Transition to:') }}
              </label>
              <select
                v-model="transitionToStatusId"
                style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px"
                required
              >
                <option :value="null">{{ s__('ProjectSettings|Select a status...') }}</option>
                <option
                  v-for="status in availableTransitionStatuses"
                  :key="status.id"
                  :value="status.id.toString()"
                >
                  {{ status.name }}{{ status.description ? ' - ' + status.description : '' }}
                </option>
              </select>
            </div>
            <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end">
              <button class="btn-secondary" @click="closeStatusDeletionTransitionModal">
                {{ __('Cancel') }}
              </button>
              <button
                class="btn-primary"
                :disabled="!transitionToStatusId"
                @click="confirmStatusDeletion"
              >
                {{ s__('ProjectSettings|Delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Task Type Deletion Transition Modal -->
    <Teleport to="body">
      <div v-if="showTaskCategoryDeletionTransitionModal" class="modal-overlay">
        <div class="modal-content" style="max-width: 600px">
          <div class="modal-header">
            <h3>{{ s__('ProjectSettings|Transition Tasks') }}</h3>
            <button
              class="modal-close"
              aria-label="Close"
              @click="closeTaskCategoryDeletionTransitionModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <p class="muted" style="margin-bottom: 16px">
              {{
                sprintf(
                  s__(
                    'ProjectSettings|The task category "%{taskCategory}" has tasks assigned to it. Please select a task category to transition these tasks to.'
                  ),
                  { taskCategory: deletingTaskCategory?.name || '' }
                )
              }}
            </p>
            <div
              v-if="deletingTaskCategory"
              style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 16px"
            >
              <p style="margin: 0 0 8px 0; font-weight: 500">
                {{ s__('ProjectSettings|Requirements:') }}
              </p>
              <ul style="margin: 0; padding-left: 20px">
                <li>{{ s__('ProjectSettings|Must have the same workflow') }}</li>
                <li>
                  {{
                    s__(
                      'ProjectSettings|Must have the same grading scale (or both must have no grading scale)'
                    )
                  }}
                </li>
              </ul>
            </div>
            <div v-if="deletingTaskCategory">
              <label style="display: block; margin-bottom: 8px; font-weight: 500">
                {{ s__('ProjectSettings|Transition to:') }}
              </label>
              <select
                v-model="transitionToTaskCategoryId"
                style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px"
                required
              >
                <option :value="null">
                  {{ s__('ProjectSettings|Select a task category...') }}
                </option>
                <option
                  v-for="tt in availableTransitionTaskCategories"
                  :key="tt.id"
                  :value="tt.id.toString()"
                >
                  {{ tt.name }}
                  <template v-if="tt.workflow">
                    ({{ s__('ProjectSettings|Workflow') }}: {{ tt.workflow.name }})</template
                  >
                  <template v-if="tt.gradingScale">
                    ({{ s__('ProjectSettings|Grading Scale') }}:
                    {{ tt.gradingScale.name }})</template
                  >
                  <template
                    v-else-if="!tt.gradingScale && deletingTaskCategory?.gradingScale === null"
                  >
                    ({{ s__('ProjectSettings|No Grading Scale') }})</template
                  >
                </option>
              </select>
              <p
                v-if="availableTransitionTaskCategories.length === 0"
                class="muted"
                style="margin-top: 8px; font-size: 13px"
              >
                {{
                  s__(
                    'ProjectSettings|No compatible task categories found. Please create a task category with the same workflow and grading scale.'
                  )
                }}
              </p>
            </div>
            <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end">
              <button class="btn-secondary" @click="closeTaskCategoryDeletionTransitionModal">
                {{ __('Cancel') }}
              </button>
              <button
                class="btn-primary"
                :disabled="
                  !transitionToTaskCategoryId || availableTransitionTaskCategories.length === 0
                "
                @click="confirmTaskCategoryDeletion"
              >
                {{ s__('ProjectSettings|Delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Member Removal Reassignment Modal -->
    <Teleport to="body">
      <div v-if="showMemberRemovalReassignmentModal" class="modal-overlay">
        <div class="modal-content" style="max-width: 500px">
          <div class="modal-header">
            <h3>{{ s__('ProjectSettings|Reassign Tasks') }}</h3>
            <button
              class="modal-close"
              aria-label="Close"
              @click="closeMemberRemovalReassignmentModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <p class="muted" style="margin-bottom: 16px">
              {{
                sprintf(
                  s__(
                    'ProjectSettings|The member "%{user}" has %{count} unfinished task(s) assigned. Choose a member to reassign these tasks to, or leave unassigned.'
                  ),
                  {
                    user:
                      removingMembership?.user?.name ||
                      removingMembership?.user?.username ||
                      `User ${removingMembership?.user?.id}`,
                    count: removingMembership?.unfinishedTasksCount || 0,
                  }
                )
              }}
            </p>
            <div v-if="removingMembership">
              <label style="display: block; margin-bottom: 8px; font-weight: 500">
                {{ s__('ProjectSettings|Reassign to:') }}
              </label>
              <user-select
                v-model="reassignToUserId"
                :users="availableUsersForReassignment"
                :placeholder="s__('ProjectSettings|Unassigned')"
                :project-id="projectId"
                :searchable="true"
                style="margin-bottom: 16px"
              />
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="closeMemberRemovalReassignmentModal">
                {{ __('Cancel') }}
              </button>
              <button class="btn-danger" style="margin-left: 8px" @click="confirmMemberRemoval">
                {{ s__('ProjectSettings|Remove Member') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Feature Information Modals -->
    <ProjectGradesInfoModal
      v-if="!isPersonalProject"
      :visible="showGradesInfoModal"
      @close="showGradesInfoModal = false"
    />
    <ProjectHierarchyInfoModal
      :visible="showHierarchyInfoModal"
      @close="showHierarchyInfoModal = false"
    />
    <ProjectTimeTrackingInfoModal
      :visible="showTimeTrackingInfoModal"
      @close="showTimeTrackingInfoModal = false"
    />
    <ProjectCloningInfoModal
      :visible="showCloningInfoModal"
      @close="showCloningInfoModal = false"
    />
    <ProjectBoardsInfoModal :visible="showBoardsInfoModal" @close="showBoardsInfoModal = false" />
    <ProjectChecklistsInfoModal
      :visible="showChecklistsInfoModal"
      @close="showChecklistsInfoModal = false"
    />
    <ProjectTemplatesInfoModal
      :visible="showTemplatesInfoModal"
      @close="showTemplatesInfoModal = false"
    />
    <ProjectInvitationsInfoModal
      v-if="canManageMembers && !isPersonalProject"
      :visible="showInvitationsInfoModal"
      @close="showInvitationsInfoModal = false"
    />
    <ProjectLabelsInfoModal :visible="showLabelsInfoModal" @close="showLabelsInfoModal = false" />
    <ProjectCustomReportInfoModal
      v-if="canUpdateProject && !isPersonalProject"
      :visible="showCustomReportInfoModal"
      @close="showCustomReportInfoModal = false"
    />
    <ProjectTaskCategoriesInfoModal
      :visible="showTaskCategoriesInfoModal"
      @close="showTaskCategoriesInfoModal = false"
    />
    <ProjectWorkflowsInfoModal
      :visible="showWorkflowsInfoModal"
      @close="showWorkflowsInfoModal = false"
    />
    <ProjectOwnershipTransferInfoModal
      v-if="isOwner && !isPersonalProject"
      :visible="showOwnershipTransferInfoModal"
      @close="showOwnershipTransferInfoModal = false"
    />
    <ProjectSilentHoursInfoModal
      v-if="!isPersonalProject && project?.telegramChatId"
      :visible="showSilentHoursInfoModal"
      @close="showSilentHoursInfoModal = false"
    />
  </AuthenticatedView>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useQuery, useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import NotificationSettings from './NotificationSettings.vue'
import WorkflowStatusForm from './WorkflowStatusForm.vue'
import InvitationsList from './InvitationsList.vue'
import MembersList from './MembersList.vue'
import OwnershipTransfer from './OwnershipTransfer.vue'
import TelegramGroupSettings from './TelegramGroupSettings.vue'
import ArchiveProject from './ArchiveProject.vue'
import HorizontalRelationTypesList from './HorizontalRelationTypesList.vue'
import ArchivedProjectWarning from '../common/ArchivedProjectWarning.vue'
import UserSelect from '../common/UserSelect.vue'
import SilentHoursForm from '../common/SilentHoursForm.vue'
import LabelsManager from '../common/LabelsManager.vue'
import EditIcon from '../common/icons/EditIcon.vue'
import TrashIcon from '../common/icons/TrashIcon.vue'
import EyeIcon from '../common/icons/EyeIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import SettingsIcon from '../common/icons/SettingsIcon.vue'
import CircleUserIcon from '../common/icons/CircleUserIcon.vue'
import MessageSquareIcon from '../common/icons/MessageSquareIcon.vue'
import CheckSimpleIcon from '../common/icons/CheckSimpleIcon.vue'
import TagIcon from '../common/icons/TagIcon.vue'
import DocumentFileIcon from '../common/icons/DocumentFileIcon.vue'
import ClockIcon from '../common/icons/ClockIcon.vue'
import BellPlusIcon from '../common/icons/BellPlusIcon.vue'
import FileIcon from '../common/icons/FileIcon.vue'
import NetworkIcon from '../common/icons/NetworkIcon.vue'
import ActivityIcon from '../common/icons/ActivityIcon.vue'
import CheckIcon from '../common/icons/CheckIcon.vue'
import CrownIcon from '../common/icons/CrownIcon.vue'
import FaviconMarkIcon from '../common/icons/FaviconMarkIcon.vue'
import ArchiveFileIcon from '../common/icons/ArchiveFileIcon.vue'
import TaskTemplatesManager from '../common/TaskTemplatesManager.vue'
import ProjectGradesInfoModal from './ProjectGradesInfoModal.vue'
import ProjectHierarchyInfoModal from './ProjectHierarchyInfoModal.vue'
import ProjectTimeTrackingInfoModal from './ProjectTimeTrackingInfoModal.vue'
import ProjectCloningInfoModal from './ProjectCloningInfoModal.vue'
import ProjectBoardsInfoModal from './ProjectBoardsInfoModal.vue'
import ProjectChecklistsInfoModal from './ProjectChecklistsInfoModal.vue'
import ProjectTemplatesInfoModal from './ProjectTemplatesInfoModal.vue'
import ProjectInvitationsInfoModal from './ProjectInvitationsInfoModal.vue'
import ProjectLabelsInfoModal from './ProjectLabelsInfoModal.vue'
import ProjectCustomReportInfoModal from './ProjectCustomReportInfoModal.vue'
import ProjectTaskCategoriesInfoModal from './ProjectTaskCategoriesInfoModal.vue'
import ProjectWorkflowsInfoModal from './ProjectWorkflowsInfoModal.vue'
import ProjectOwnershipTransferInfoModal from './ProjectOwnershipTransferInfoModal.vue'
import ProjectSilentHoursInfoModal from './ProjectSilentHoursInfoModal.vue'
import ReportConfigurationPanel from '../profile/ReportConfigurationPanel.vue'
import RegularReportsPanel from '../profile/RegularReportsPanel.vue'
import AppHeader from '../common/AppHeader.vue'
import AlertBanner from '../common/AlertBanner.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import SettingsSidebarNav from '../common/SettingsSidebarNav.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { useRegularReport } from '@/composables/useRegularReport'
import '../../styles/settings-toggle-with-info.css'
import '../../styles/mobile-underlined-checklist.css'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'
import {
  readSidebarCollapsedPreference,
  writeSidebarCollapsedPreference,
} from '@/utils/sidebarCollapsePreference'

const props = defineProps({ projectId: String, chatId: String, fromId: String })
const PROJECT_SETTINGS_ACTIVE_SECTION_SESSION_KEY = `projectSettingsActiveSection:${props.projectId}`

// Feature flag for S3 attachments
const s3AttachmentsEnabled = computed(() => {
  return window.gon?.s3_attachments_enabled === true
})

const loading = ref(true)
const error = ref('')
const project = ref(null)
const taskCategories = ref([])
const workflows = ref([])
const memberships = ref([])
const membershipTelegramStateById = ref({})
const telegramStatusExpanded = ref(false)
const telegramStatusLoadState = ref('idle')
const telegramStatusErrorMessage = ref('')
const TELEGRAM_STATUS_TIMEOUT_MS = 5000
const TELEGRAM_STATUS_TIMEOUT_CODE = 'TELEGRAM_STATUS_TIMEOUT'
const canManageMembers = ref(false)
const canUpdateProject = ref(false)
const canArchiveProject = ref(false)
const isOwner = ref(false)
const projectArchived = ref(false)
const gradesEnabled = ref(false)
const gradingScales = ref([])

// Timezone settings
const timezones = ref([])
const loadingTimezones = ref(true)
const projectLanguage = ref(null)
const originalProjectLanguage = ref(null)
const projectTimezone = ref(null)
const originalProjectTimezone = ref(null)
const projectGradesEnabled = ref(false)
const originalProjectGradesEnabled = ref(false)
const projectVerticalRelationsEnabled = ref(false)
const originalProjectVerticalRelationsEnabled = ref(false)
const projectTimeTrackingEnabled = ref(false)
const originalProjectTimeTrackingEnabled = ref(false)
const projectTaskCloningEnabled = ref(false)
const originalProjectTaskCloningEnabled = ref(false)
const projectBoardsEnabled = ref(false)
const originalProjectBoardsEnabled = ref(false)
const projectChecklistsEnabled = ref(false)
const originalProjectChecklistsEnabled = ref(false)

// Feature info modal state
const showGradesInfoModal = ref(false)
const showHierarchyInfoModal = ref(false)
const showTimeTrackingInfoModal = ref(false)
const showCloningInfoModal = ref(false)
const showBoardsInfoModal = ref(false)
const showChecklistsInfoModal = ref(false)
const showTemplatesInfoModal = ref(false)
const showInvitationsInfoModal = ref(false)
const showLabelsInfoModal = ref(false)
const showCustomReportInfoModal = ref(false)
const showTaskCategoriesInfoModal = ref(false)
const showWorkflowsInfoModal = ref(false)
const showOwnershipTransferInfoModal = ref(false)
const showSilentHoursInfoModal = ref(false)
const projectTemplatesEnabled = ref(false)
const originalProjectTemplatesEnabled = ref(false)
const projectGroupChatNotificationsEnabled = ref(true)
const originalProjectGroupChatNotificationsEnabled = ref(true)
const projectSilentHoursStartHour = ref(null)
const originalProjectSilentHoursStartHour = ref(null)
const projectSilentHoursStartMinute = ref(null)
const originalProjectSilentHoursStartMinute = ref(null)
const projectSilentHoursEndHour = ref(null)
const originalProjectSilentHoursEndHour = ref(null)
const projectSilentHoursEndMinute = ref(null)
const originalProjectSilentHoursEndMinute = ref(null)
// Use composable for deadline report
const deadlineReport = useRegularReport({
  initialHour: null,
  initialMinute: null,
  initialDays: [],
  preferredLanguage: projectLanguage, // Pass ref so it's reactive
  translationPrefix: 'ProjectSettings',
})

// Use composable for completed tasks report
const completedTasksReport = useRegularReport({
  initialHour: null,
  initialMinute: null,
  initialDays: [],
  preferredLanguage: projectLanguage, // Pass ref so it's reactive
  translationPrefix: 'ProjectSettings',
})

// Expose refs for template compatibility
const projectDeadlineReportHour = deadlineReport.localHour
const projectDeadlineReportMinute = deadlineReport.localMinute
const projectDeadlineReportDays = deadlineReport.localDays
const originalProjectDeadlineReportHour = deadlineReport.originalHour
const originalProjectDeadlineReportMinute = deadlineReport.originalMinute
const originalProjectDeadlineReportDays = deadlineReport.originalDays

const projectCompletedTasksReportHour = completedTasksReport.localHour
const projectCompletedTasksReportMinute = completedTasksReport.localMinute
const projectCompletedTasksReportDays = completedTasksReport.localDays
const originalProjectCompletedTasksReportHour = completedTasksReport.originalHour
const originalProjectCompletedTasksReportMinute = completedTasksReport.originalMinute
const originalProjectCompletedTasksReportDays = completedTasksReport.originalDays
const projectRequiredFields = ref([])
const originalProjectRequiredFields = ref([])
const savingRequiredFields = ref(false)
const saveRequiredFieldsError = ref('')
const saveRequiredFieldsSuccess = ref(false)
const saving = ref(false)
const saveError = ref('')
const saveSuccess = ref(false)
const savingReports = ref(false)
const saveReportsError = ref('')
const saveReportsSuccess = ref(false)

// Title editing
const editingTitle = ref(false)
const editedTitle = ref('')
const savingTitle = ref(false)
const titleError = ref('')
const editedCode = ref('')
const originalCode = ref('')
const savingCode = ref(false)
const codeError = ref('')
const CODE_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9_-]{3,12}$/
const canSetCode = computed(() => canUpdateProject.value && !originalCode.value)
const codeLocked = computed(() => !!originalCode.value)
const isCodeFormatValid = computed(() => {
  const trimmedCode = editedCode.value.trim()
  return trimmedCode.length === 0 || CODE_PATTERN.test(trimmedCode)
})

// Description editing
const editingDescription = ref(false)
const editedDescription = ref('')
const savingDescription = ref(false)
const descriptionError = ref('')
const autoSaving = ref(false)
const basicSettingsAlert = ref(null)
const membersListAlert = ref(null)
const savingSilentHours = ref(false)
const silentHoursAlert = ref(null)
const autoSaveTimeout = ref(null)
const formInitialized = ref(false)
const isPopulatingForm = ref(false)
const activeSettingsSection = ref('basicSettings')
const pendingSettingsSection = ref(null)
const canCollapseSidebar = ref(false)
const isSidebarCollapsed = ref(false)
const isMobileLayout = ref(false)
const isMobileSidebarExpanded = ref(false)
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)
const labelsManagerRef = ref(null)
const taskTemplatesManagerRef = ref(null)
const horizontalRelationTypesRef = ref(null)

// Collapsible sections state
const expandedSections = ref({
  basicSettings: true,
  requiredFields: false,
  members: false,
  invitations: false,
  ownershipTransfer: false,
  regularReports: false,
  notifications: false,
  labels: false,
  taskTemplates: false,
  horizontalRelationTypes: false,
  taskCategories: false,
  workflows: false,
  gradingScales: false,
  customReport: false,
})

const setActiveProjectSection = section => {
  if (!section) return
  const activeChanged = activeSettingsSection.value !== section
  const nextExpanded = { ...expandedSections.value }
  let expandedChanged = false
  Object.keys(nextExpanded).forEach(key => {
    const shouldBeExpanded = key === section
    if (nextExpanded[key] !== shouldBeExpanded) {
      nextExpanded[key] = shouldBeExpanded
      expandedChanged = true
    }
  })

  if (!activeChanged && !expandedChanged) return

  activeSettingsSection.value = section
  if (expandedChanged) {
    expandedSections.value = nextExpanded
  }

  try {
    sessionStorage.setItem(PROJECT_SETTINGS_ACTIVE_SECTION_SESSION_KEY, section)
  } catch {}
}

const projectSidebarItems = computed(() => {
  const items = [
    {
      key: 'basicSettings',
      label: s__('ProjectSettings|General'),
      icon: SettingsIcon,
      expanded: expandedSections.value.basicSettings,
    },
    {
      key: 'members',
      label: s__('ProjectSettings|Members'),
      icon: CircleUserIcon,
      expanded: expandedSections.value.members,
      hidden: !canManageMembers.value || isPersonalProject.value,
    },
    {
      key: 'invitations',
      label: s__('ProjectSettings|Invitations'),
      icon: MessageSquareIcon,
      expanded: expandedSections.value.invitations,
      hidden: !canManageMembers.value || isPersonalProject.value,
    },
    {
      key: 'requiredFields',
      label: s__('ProjectSettings|Required Fields'),
      icon: CheckSimpleIcon,
      expanded: expandedSections.value.requiredFields,
      hidden: !canUpdateProject.value || isPersonalProject.value,
    },
    {
      key: 'labels',
      label: s__('ProjectSettings|Labels'),
      icon: TagIcon,
      expanded: expandedSections.value.labels,
    },
    {
      key: 'customReport',
      label: s__('ProjectSettings|Custom Report'),
      icon: DocumentFileIcon,
      expanded: expandedSections.value.customReport,
      hidden: !canUpdateProject.value || isPersonalProject.value || !project.value,
    },
    {
      key: 'regularReports',
      label: s__('ProjectSettings|Telegram Regular Reports'),
      icon: ClockIcon,
      expanded: expandedSections.value.regularReports,
      hidden: !canUpdateProject.value || isPersonalProject.value || !project.value?.telegramChatId,
    },
    {
      key: 'notifications',
      label: s__('ProjectSettings|Telegram Notifications'),
      icon: BellPlusIcon,
      expanded: expandedSections.value.notifications,
      hidden: !canUpdateProject.value || isPersonalProject.value || !project.value?.telegramChatId,
    },
    {
      key: 'taskTemplates',
      label: s__('ProjectSettings|Task Templates'),
      icon: FileIcon,
      expanded: expandedSections.value.taskTemplates,
      hidden: !projectTemplatesEnabled.value,
    },
    {
      key: 'horizontalRelationTypes',
      label: s__('ProjectSettings|Horizontal Task Relations'),
      icon: NetworkIcon,
      expanded: expandedSections.value.horizontalRelationTypes,
    },
    {
      key: 'taskCategories',
      label: s__('ProjectSettings|Task Categories'),
      icon: FileIcon,
      expanded: expandedSections.value.taskCategories,
    },
    {
      key: 'workflows',
      label: s__('ProjectSettings|Workflows'),
      icon: ActivityIcon,
      expanded: expandedSections.value.workflows,
    },
    {
      key: 'gradingScales',
      label: s__('ProjectSettings|Grading Scales'),
      icon: CheckIcon,
      expanded: expandedSections.value.gradingScales,
      hidden: !gradesEnabled.value || isPersonalProject.value,
    },
    {
      key: 'ownershipTransfer',
      label: s__('ProjectSettings|Ownership Transfer'),
      icon: CrownIcon,
      expanded: expandedSections.value.ownershipTransfer,
      hidden: !isOwner.value || isPersonalProject.value,
    },
    {
      key: 'archiveProject',
      label: s__('ProjectSettings|Archive Project'),
      icon: ArchiveFileIcon,
      expanded: activeSettingsSection.value === 'archiveProject',
      hidden: !canArchiveProject.value,
    },
    {
      key: 'go-setup',
      type: 'link',
      label: s__('ProjectSettings|Setup'),
      url: setupUrl.value,
      icon: InfoIcon,
    },
    {
      key: 'go-project',
      type: 'link',
      label: s__('ProjectSettings|Project'),
      url: tasksUrl.value,
      icon: NetworkIcon,
    },
    {
      key: 'go-dashboard',
      type: 'link',
      label: s__('Dashboard|Dashboard'),
      url: dashboardUrl.value,
      icon: FaviconMarkIcon,
    },
  ]

  return items.filter(item => !item.hidden)
})

const activeProjectPanelTitle = computed(() => {
  const activeItem = projectSidebarItems.value.find(
    item => item.type !== 'link' && item.key === activeSettingsSection.value
  )
  return activeItem?.label || s__('ProjectSettings|Settings')
})

const showHeaderCreateAction = computed(() => {
  return [
    'labels',
    'taskTemplates',
    'horizontalRelationTypes',
    'taskCategories',
    'workflows',
    'gradingScales',
  ].includes(activeSettingsSection.value)
})

const headerCreateActionLabel = computed(() => {
  if (activeSettingsSection.value === 'labels') {
    return s__('ProjectSettings|Add Label')
  }
  if (activeSettingsSection.value === 'taskTemplates') {
    return s__('ProjectSettings|New Template')
  }
  if (activeSettingsSection.value === 'horizontalRelationTypes') {
    return s__('ProjectSettings|Add Relation Type')
  }
  if (activeSettingsSection.value === 'taskCategories') {
    return s__('ProjectSettings|Add Task Category')
  }
  if (activeSettingsSection.value === 'workflows') {
    return s__('ProjectSettings|Add Workflow')
  }
  if (activeSettingsSection.value === 'gradingScales') {
    return s__('ProjectSettings|Add Grading Scale')
  }
  return s__('Dashboard|Create Task')
})

const syncActiveProjectSection = items => {
  const visibleKeys = items.filter(item => item.type !== 'link').map(item => item.key)
  if (visibleKeys.length === 0) return

  if (pendingSettingsSection.value && visibleKeys.includes(pendingSettingsSection.value)) {
    setActiveProjectSection(pendingSettingsSection.value)
    pendingSettingsSection.value = null
    return
  }

  if (!visibleKeys.includes(activeSettingsSection.value)) {
    setActiveProjectSection(visibleKeys[0])
  }
}

const scrollToProjectSection = async section => {
  if (typeof window === 'undefined' || window.innerWidth >= 1024) return

  await nextTick()

  const sectionElement = document.querySelector(`[data-settings-section="${section}"]`)
  if (!(sectionElement instanceof HTMLElement)) return
  const panelElement = sectionElement.closest('.dashboard-panel')
  const panelHeaderElement = panelElement?.querySelector('.dashboard-panel-header')
  const targetElement =
    panelHeaderElement instanceof HTMLElement ? panelHeaderElement : sectionElement

  let topOffset = 12
  const headerElement = document.querySelector('.app-header')
  if (headerElement instanceof HTMLElement) {
    const headerStyles = window.getComputedStyle(headerElement)
    if (headerStyles.position === 'sticky') {
      const top = parseFloat(headerStyles.top || '0') || 0
      topOffset = Math.ceil(headerElement.offsetHeight + top + 12)
    }
  }

  const targetTop = targetElement.getBoundingClientRect().top + window.scrollY - topOffset
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' })
}

const openProjectSection = section => {
  setActiveProjectSection(section)
  if (isMobileLayout.value) {
    isMobileSidebarExpanded.value = false
  }
  scrollToProjectSection(section)
}

const toggleMobileSidebar = () => {
  if (!isMobileLayout.value) return
  isMobileSidebarExpanded.value = !isMobileSidebarExpanded.value
}

const handleHeaderCreateAction = () => {
  if (activeSettingsSection.value === 'labels') {
    labelsManagerRef.value?.openCreateForm?.()
    return
  }
  if (activeSettingsSection.value === 'taskTemplates') {
    taskTemplatesManagerRef.value?.openCreateModal?.()
    return
  }
  if (activeSettingsSection.value === 'horizontalRelationTypes') {
    horizontalRelationTypesRef.value?.openCreateModal?.()
    return
  }
  if (activeSettingsSection.value === 'taskCategories') {
    showNewTaskCategoryForm.value = true
    return
  }
  if (activeSettingsSection.value === 'workflows') {
    showNewWorkflowForm.value = true
    return
  }
  if (activeSettingsSection.value === 'gradingScales') {
    showNewGradingScaleForm.value = true
  }
}

const toggleSidebarCollapse = () => {
  if (!canCollapseSidebar.value) return
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  writeSidebarCollapsedPreference(isSidebarCollapsed.value)
}

const toggleFullWidth = () => {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
}

const checkSettingsLayoutCapabilities = () => {
  if (typeof window === 'undefined') return

  canCollapseSidebar.value = window.innerWidth >= 1024
  isMobileLayout.value = window.innerWidth < 1024
  canToggleFullWidth.value = window.innerWidth >= 1024

  if (!canCollapseSidebar.value) {
    isSidebarCollapsed.value = false
  }
  if (!isMobileLayout.value) {
    isMobileSidebarExpanded.value = false
  }
  isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
}

onMounted(() => {
  try {
    const storedActiveSection = sessionStorage.getItem(PROJECT_SETTINGS_ACTIVE_SECTION_SESSION_KEY)
    if (storedActiveSection) {
      pendingSettingsSection.value = storedActiveSection
    }

    isSidebarCollapsed.value = readSidebarCollapsedPreference()
  } catch {}

  checkSettingsLayoutCapabilities()
  window.addEventListener('resize', checkSettingsLayoutCapabilities)
  syncActiveProjectSection(projectSidebarItems.value)
  watch(projectSidebarItems, syncActiveProjectSection)
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', checkSettingsLayoutCapabilities)
  }
})

const hasProjectTimezone = computed(() => {
  return !!projectTimezone.value
})

const hasProjectSilentHours = computed(() => {
  return !!(
    projectSilentHoursStartHour.value ||
    projectSilentHoursStartMinute.value !== null ||
    projectSilentHoursEndHour.value ||
    projectSilentHoursEndMinute.value !== null
  )
})

const hasProjectChanges = computed(() => {
  return (
    projectLanguage.value !== originalProjectLanguage.value ||
    projectTimezone.value !== originalProjectTimezone.value ||
    projectGradesEnabled.value !== originalProjectGradesEnabled.value ||
    projectVerticalRelationsEnabled.value !== originalProjectVerticalRelationsEnabled.value ||
    projectTimeTrackingEnabled.value !== originalProjectTimeTrackingEnabled.value ||
    projectTaskCloningEnabled.value !== originalProjectTaskCloningEnabled.value ||
    projectBoardsEnabled.value !== originalProjectBoardsEnabled.value ||
    projectChecklistsEnabled.value !== originalProjectChecklistsEnabled.value ||
    projectTemplatesEnabled.value !== originalProjectTemplatesEnabled.value ||
    projectGroupChatNotificationsEnabled.value !==
      originalProjectGroupChatNotificationsEnabled.value
  )
})

const hasProjectSilentHoursChanges = computed(() => {
  return (
    projectSilentHoursStartHour.value !== originalProjectSilentHoursStartHour.value ||
    projectSilentHoursStartMinute.value !== originalProjectSilentHoursStartMinute.value ||
    projectSilentHoursEndHour.value !== originalProjectSilentHoursEndHour.value ||
    projectSilentHoursEndMinute.value !== originalProjectSilentHoursEndMinute.value
  )
})

const hasRegularReportsChanges = computed(() => {
  return deadlineReport.hasChanges.value || completedTasksReport.hasChanges.value
})

const hasRequiredFieldsChanges = computed(() => {
  const normalizeArray = arr => {
    if (!arr || !Array.isArray(arr)) return []
    return [...arr].sort()
  }
  return (
    JSON.stringify(normalizeArray(projectRequiredFields.value)) !==
    JSON.stringify(normalizeArray(originalProjectRequiredFields.value))
  )
})

const isPersonalProject = computed(() => {
  return project.value?.isPersonal === true || project.value?.is_personal === true
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const setupUrl = computed(() => {
  let url = `/projects/${props.projectId}/setup`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const attachmentsUrl = computed(() => {
  let url = `/projects/${props.projectId}/dashboard?tab=attachments`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '&' + params.join('&')
  return url
})

const tasksUrl = computed(() => {
  // Try to get stored URL with filters from sessionStorage
  try {
    const storedUrl = sessionStorage.getItem(`taskIndexUrl_${props.projectId}`)
    if (storedUrl) {
      // Parse the stored URL and preserve chat_id/from_id from props if needed
      const storedUrlObj = new URL(storedUrl, window.location.origin)
      const currentParams = new URLSearchParams(storedUrlObj.search)

      // Update chat_id and from_id if provided in props
      if (props.chatId) {
        currentParams.set('chat_id', props.chatId)
      }
      if (props.fromId) {
        currentParams.set('from_id', props.fromId)
      }

      return (
        storedUrlObj.pathname + (currentParams.toString() ? '?' + currentParams.toString() : '')
      )
    }
  } catch (e) {
    console.warn('Failed to get stored task index URL:', e)
  }

  // Fallback to default URL
  let url = `/projects/${props.projectId}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  const items = [{ label: s__('Dashboard|Main'), url: dashboardUrl.value }]
  if (project.value?.title) {
    items.push({ label: project.value.title, url: tasksUrl.value })
  } else {
    items.push({ label: s__('ProjectSettings|Project') })
  }
  items.push({ label: s__('ProjectSettings|Project Settings') })
  return items
})

// Task category form state
const showNewTaskCategoryForm = ref(false)
const newTaskCategory = ref({ name: '', workflowId: undefined, gradingScaleId: null })
const editingTaskCategory = ref(null)
const originalGradingScaleId = ref(null) // Store original grading scale when editing starts
const originalWorkflowId = ref(null) // Store original workflow when editing starts
const submittingTaskCategory = ref(false)
const taskCategoryError = ref('')

// Grade transition modal state
const showGradeTransitionModal = ref(false)
const gradeMappings = ref({}) // { oldGradeId: newGradeId }
const transitionOldScale = ref(null)
const transitionNewScale = ref(null)
const transitionTaskCategory = ref(null)

// Workflow status transition modal state
const showWorkflowStatusTransitionModal = ref(false)
const workflowStatusMappings = ref({}) // { oldStatusId: newStatusId }
const transitionOldWorkflow = ref(null)
const transitionNewWorkflow = ref(null)

const canCreateTaskCategory = computed(() => newTaskCategory.value.name.trim().length > 0)
const canUpdateTaskCategory = computed(
  () => editingTaskCategory.value && editingTaskCategory.value.name.trim().length > 0
)

// Computed: sorted grades for grade transition modal
const sortedOldGrades = computed(() => {
  if (!transitionOldScale.value?.grades) return []
  return [...transitionOldScale.value.grades].sort((a, b) => a.position - b.position)
})

const sortedNewGrades = computed(() => {
  if (!transitionNewScale.value?.grades) return []
  return [...transitionNewScale.value.grades].sort((a, b) => a.position - b.position)
})

// Computed: sorted workflow statuses for workflow status transition modal
const sortedOldStatuses = computed(() => {
  if (!transitionOldWorkflow.value?.statuses) return []
  return [...transitionOldWorkflow.value.statuses].sort((a, b) => a.position - b.position)
})

const sortedNewStatuses = computed(() => {
  if (!transitionNewWorkflow.value?.statuses) return []
  return [...transitionNewWorkflow.value.statuses].sort((a, b) => a.position - b.position)
})

// Workflow form state
const showNewWorkflowForm = ref(false)
const newWorkflow = ref({ name: '', description: '', isDefault: false })
const editingWorkflow = ref(null)
const viewingWorkflow = ref(null)
const submittingWorkflow = ref(false)
const workflowError = ref('')

const canCreateWorkflow = computed(() => newWorkflow.value.name.trim().length > 0)
const canUpdateWorkflow = computed(
  () => editingWorkflow.value && editingWorkflow.value.name.trim().length > 0
)

// Status form state
const showNewStatusForm = ref(false)
const newStatus = ref({
  name: '',
  description: '',
  position: 0,
  color: '#6B7280',
  isInitial: false,
  isFinal: false,
  requiresCompletedChecklists: false,
})
const editingStatus = ref(null)
const submittingStatus = ref(false)
const statusError = ref('')

// Status deletion transition modal state
const showStatusDeletionTransitionModal = ref(false)
const deletingStatus = ref(null)
const transitionToStatusId = ref(null)

const canCreateStatus = computed(
  () => newStatus.value.name.trim().length > 0 && /^#[0-9A-F]{6}$/i.test(newStatus.value.color)
)
const canUpdateStatus = computed(
  () =>
    editingStatus.value &&
    editingStatus.value.name.trim().length > 0 &&
    /^#[0-9A-F]{6}$/i.test(editingStatus.value.color)
)

const sortedStatuses = computed(() => {
  if (!viewingWorkflow.value?.statuses) return []
  return [...viewingWorkflow.value.statuses].sort((a, b) => a.position - b.position)
})

// Transition form state
const showNewTransitionForm = ref(false)
const newTransition = ref({ sourceStatusIds: [], targetStatusId: '', name: '', isGlobal: false })
const submittingTransition = ref(false)
const transitionError = ref('')

const canCreateTransition = computed(() => {
  if (!newTransition.value.targetStatusId) return false
  if (newTransition.value.isGlobal) return true
  return (
    newTransition.value.sourceStatusIds.length > 0 &&
    !newTransition.value.sourceStatusIds.includes(newTransition.value.targetStatusId)
  )
})

// Grading scale form state
const showNewGradingScaleForm = ref(false)
const newGradingScale = ref({ name: '' })
const editingGradingScale = ref(null)
const viewingGradingScale = ref(null)
const submittingGradingScale = ref(false)
const gradingScaleError = ref('')

const canCreateGradingScale = computed(() => newGradingScale.value.name.trim().length > 0)
const canUpdateGradingScale = computed(
  () => editingGradingScale.value && editingGradingScale.value.name.trim().length > 0
)

// Grade form state
const showNewGradeForm = ref(false)
const newGrade = ref({ value: '', description: '' })
const editingGrade = ref(null)
const submittingGrade = ref(false)
const gradeError = ref('')

const canCreateGrade = computed(
  () => newGrade.value.value.trim().length > 0 && newGrade.value.value.length <= 3
)
const canUpdateGrade = computed(
  () =>
    editingGrade.value &&
    editingGrade.value.value.trim().length > 0 &&
    editingGrade.value.value.length <= 3
)

const sortedGrades = computed(() => {
  if (!viewingGradingScale.value?.grades) return []
  return [...viewingGradingScale.value.grades].sort((a, b) => a.position - b.position)
})

// Helper to get task categories for a workflow
function taskCategoriesForWorkflow(workflowId) {
  return taskCategories.value.filter(tt => tt.workflow?.id === workflowId)
}

// Computed: available statuses for transition (excluding the one being deleted)
const availableTransitionStatuses = computed(() => {
  if (!deletingStatus.value || !viewingWorkflow.value) return []
  return viewingWorkflow.value.statuses.filter(s => s.id !== deletingStatus.value.id)
})

// Task category deletion transition modal state
const showTaskCategoryDeletionTransitionModal = ref(false)
const deletingTaskCategory = ref(null)
const transitionToTaskCategoryId = ref(null)

// Member removal reassignment modal state
const showMemberRemovalReassignmentModal = ref(false)
const removingMembership = ref(null)
const reassignToUserId = ref(null)

// Computed: available task categories for transition (same workflow and grading scale)
const availableTransitionTaskCategories = computed(() => {
  if (!deletingTaskCategory.value) return []
  return taskCategories.value.filter(tt => {
    // Exclude the one being deleted
    if (tt.id === deletingTaskCategory.value.id) return false
    // Must have same workflow
    if (tt.workflow?.id !== deletingTaskCategory.value.workflow?.id) return false
    // Must have same grading scale (both nil or both same ID)
    const deletingScaleId = deletingTaskCategory.value.gradingScale?.id || null
    const ttScaleId = tt.gradingScale?.id || null
    if (deletingScaleId !== ttScaleId) return false
    return true
  })
})

// Format user for display in selectboxes: shows username and name
function formatUserForSelect(user) {
  if (!user) return '—'
  const parts = []
  if (user.username) parts.push('@' + user.username)
  if (user.name) parts.push(user.name)
  if (parts.length > 0) return parts.join(' ')
  return 'User ' + user.id
}

// Base project query - always loaded
const BASE_PROJECT_QUERY = gql`
  query ProjectShowBase($id: ID!, $uid: BigInt) {
    project(id: $id) {
      id
      title
      description
      isPersonal
      telegramChatId
      preferredLanguage
      preferredTimezone
      canManageMembers
      canUpdateProject
      canUpdateTitle
      canUpdateDescription
      canArchiveProject
      archived
      code
      gradesEnabled
      verticalRelationsEnabled
      timeTrackingEnabled
      taskCloningEnabled
      boardsEnabled
      checklistsEnabled
      templatesEnabled
      groupChatNotificationsEnabled
      silentHours
      deadlineReportTime
      deadlineReportDays
      completedTasksReportTime
      completedTasksReportDays
      requiredFields
      defaultWorkflow {
        id
        name
      }
    }
    currentUser {
      id
      name
      username
    }
    userByTelegram(telegramUserId: $uid) {
      id
    }
  }
`

// Lazy-loaded queries for each section
const MEMBERS_QUERY = gql`
  query ProjectMembers($id: ID!) {
    project(id: $id) {
      id
      memberships {
        id
        role
        projectRole
        unfinishedTasksCount
        user {
          id
          username
          name
        }
      }
    }
  }
`

const MEMBERS_TELEGRAM_STATUS_QUERY = gql`
  query ProjectMembersTelegramStatus($id: ID!) {
    project(id: $id) {
      id
      memberships {
        id
        role
        inTelegramGroup
        hasDmAccess
      }
    }
  }
`

const TASK_CATEGORIES_QUERY = gql`
  query ProjectTaskCategories($id: ID!) {
    project(id: $id) {
      id
      taskCategories {
        id
        name
        workflow {
          id
          name
        }
        gradingScale {
          id
          name
        }
      }
    }
  }
`

const WORKFLOWS_QUERY = gql`
  query ProjectWorkflows($id: ID!) {
    project(id: $id) {
      id
      workflows {
        id
        name
        description
        isDefault
        statuses {
          id
          name
          description
          position
          color
          isInitial
          isFinal
          requiresCompletedChecklists
        }
        transitions {
          id
          name
          isGlobal
          sourceStatuses {
            id
            name
          }
          targetStatus {
            id
            name
          }
        }
      }
    }
  }
`

const GRADING_SCALES_QUERY = gql`
  query ProjectGradingScales($id: ID!) {
    project(id: $id) {
      id
      gradingScales {
        id
        name
        position
        grades {
          id
          value
          description
          position
          gradingScaleId
        }
      }
    }
  }
`

// Get viewer's Telegram user ID
const viewerUid = (() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  const fromId = props.fromId
  return (tg && tg.user && tg.user.id) || fromId || null
})()

// Fetch timezones
const TIMEZONES_Q = gql`
  query GetTimezones {
    timezoneOptions {
      value
      label
    }
  }
`

const { result: timezonesResult, loading: tzLoading } = useQuery(TIMEZONES_Q)
watch(
  tzLoading,
  val => {
    loadingTimezones.value = val
  },
  { immediate: true }
)
watch(
  timezonesResult,
  data => {
    if (data?.timezoneOptions) {
      timezones.value = data.timezoneOptions
    }
  },
  { immediate: true }
)

// Base project query - always loaded
const {
  result: baseResult,
  loading: baseLoading,
  error: baseError,
  refetch: refetchBase,
} = useQuery(
  BASE_PROJECT_QUERY,
  () => ({
    id: props.projectId,
    uid: viewerUid ? parseInt(viewerUid) : null,
  }),
  () => ({
    enabled: !!props.projectId,
  })
)

// Current user for AppHeader
const currentUser = computed(() => baseResult.value?.currentUser || null)

// Lazy-loaded queries - only fetch when section is expanded
// useLazyQuery doesn't execute automatically - we call load() manually when section expands
const {
  load: loadMembers,
  result: membersResult,
  loading: membersLoading,
  refetch: refetchMembers,
} = useLazyQuery(
  MEMBERS_QUERY,
  () => ({
    id: props.projectId,
  }),
  {
    fetchPolicy: 'cache-and-network', // Always check network for fresh data
  }
)

const {
  load: loadMembersTelegramStatusQuery,
  result: membersTelegramStatusResult,
  loading: membersTelegramStatusLoading,
  refetch: refetchMembersTelegramStatus,
} = useLazyQuery(
  MEMBERS_TELEGRAM_STATUS_QUERY,
  () => ({
    id: props.projectId,
  }),
  {
    fetchPolicy: 'cache-and-network',
  }
)

const isTelegramStatusLoaded = computed(() => telegramStatusLoadState.value === 'success')
const isTelegramStatusLoading = computed(() => telegramStatusLoadState.value === 'loading')

function createTelegramStatusTimeoutError() {
  const error = new Error('Telegram status loading timed out')
  error.code = TELEGRAM_STATUS_TIMEOUT_CODE
  return error
}

function timeoutPromise(ms) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    const signal = AbortSignal.timeout(ms)
    return new Promise((_, reject) => {
      signal.addEventListener('abort', () => reject(createTelegramStatusTimeoutError()), {
        once: true,
      })
    })
  }

  return new Promise((_, reject) => {
    const startedAt = Date.now()
    const checkTimeout = () => {
      if (Date.now() - startedAt >= ms) {
        reject(createTelegramStatusTimeoutError())
        return
      }
      requestAnimationFrame(checkTimeout)
    }
    requestAnimationFrame(checkTimeout)
  })
}

async function awaitWithTimeout(promise, timeoutMs) {
  return Promise.race([promise, timeoutPromise(timeoutMs)])
}

function mergeTelegramStateIntoMemberships(sourceMemberships = []) {
  return sourceMemberships.map(membership => {
    const state = membershipTelegramStateById.value[String(membership.id)]
    if (!state) return membership
    return {
      ...membership,
      inTelegramGroup: state.inTelegramGroup,
      hasDmAccess: state.hasDmAccess,
    }
  })
}

async function loadMembersTelegramStatus() {
  if (isTelegramStatusLoading.value || isTelegramStatusLoaded.value) return
  telegramStatusLoadState.value = 'loading'
  telegramStatusErrorMessage.value = ''

  if (!membersResult.value && !membersLoading.value) {
    await loadMembers()
  }

  try {
    if (!membersTelegramStatusResult.value) {
      await awaitWithTimeout(loadMembersTelegramStatusQuery(), TELEGRAM_STATUS_TIMEOUT_MS)
    } else if (refetchMembersTelegramStatus) {
      await awaitWithTimeout(refetchMembersTelegramStatus(), TELEGRAM_STATUS_TIMEOUT_MS)
    }
    telegramStatusLoadState.value = 'success'
  } catch (e) {
    if (e?.code === TELEGRAM_STATUS_TIMEOUT_CODE) {
      telegramStatusLoadState.value = 'timeout'
      return
    }

    telegramStatusLoadState.value = 'error'
    telegramStatusErrorMessage.value = e?.message || ''
  }
}

const {
  load: loadTaskCategories,
  result: taskCategoriesResult,
  loading: taskCategoriesLoading,
} = useLazyQuery(TASK_CATEGORIES_QUERY, () => ({
  id: props.projectId,
}))

const {
  load: loadWorkflows,
  result: workflowsResult,
  loading: workflowsLoading,
} = useLazyQuery(WORKFLOWS_QUERY, () => ({
  id: props.projectId,
}))

const {
  load: loadGradingScales,
  result: gradingScalesResult,
  loading: gradingScalesLoading,
} = useLazyQuery(GRADING_SCALES_QUERY, () => ({
  id: props.projectId,
}))

// Watch for base query results
watch(
  baseLoading,
  v => {
    loading.value = !!v
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(
  baseError,
  e => {
    if (e) error.value = e.message || 'Failed to load project'
  },
  { immediate: true }
)
watch(
  baseResult,
  data => {
    if (data?.project) {
      project.value = data.project
      canManageMembers.value = data.project.canManageMembers || false
      canUpdateProject.value = data.project.canUpdateProject || false
      canArchiveProject.value = data.project.canArchiveProject || false
      projectArchived.value = data.project.archived || false

      // Check authorization - only owners and admins can access project settings
      if (!data.project.canUpdateProject) {
        // Redirect to access denied page
        const params = []
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        const queryString = params.length > 0 ? '?' + params.join('&') : ''
        window.location.href = `/projects/${props.projectId}/access_denied${queryString}`
        return
      }

      // Check if current user is owner (canArchiveProject implies owner)
      isOwner.value = data.project.canArchiveProject || false
      gradesEnabled.value = data.project.gradesEnabled || false
      projectLanguage.value = data.project.preferredLanguage || 'en' // Default to 'en' if null
      originalProjectLanguage.value = data.project.preferredLanguage || 'en'
      projectTimezone.value = data.project.preferredTimezone
      originalProjectTimezone.value = data.project.preferredTimezone
      projectGradesEnabled.value = data.project.gradesEnabled || false
      originalProjectGradesEnabled.value = data.project.gradesEnabled || false
      projectVerticalRelationsEnabled.value = data.project.verticalRelationsEnabled || false
      originalProjectVerticalRelationsEnabled.value = data.project.verticalRelationsEnabled || false
      projectTimeTrackingEnabled.value = data.project.timeTrackingEnabled || false
      originalProjectTimeTrackingEnabled.value = data.project.timeTrackingEnabled || false
      projectTaskCloningEnabled.value = data.project.taskCloningEnabled || false
      originalProjectTaskCloningEnabled.value = data.project.taskCloningEnabled || false
      projectBoardsEnabled.value = data.project.boardsEnabled || false
      originalProjectBoardsEnabled.value = data.project.boardsEnabled || false
      projectChecklistsEnabled.value = data.project.checklistsEnabled || false
      originalProjectChecklistsEnabled.value = data.project.checklistsEnabled || false
      editedCode.value = data.project.code || ''
      originalCode.value = data.project.code || ''
      codeError.value = ''
      projectTemplatesEnabled.value = data.project.templatesEnabled || false
      originalProjectTemplatesEnabled.value = data.project.templatesEnabled || false
      projectGroupChatNotificationsEnabled.value =
        data.project.groupChatNotificationsEnabled !== false
      originalProjectGroupChatNotificationsEnabled.value =
        data.project.groupChatNotificationsEnabled !== false

      // Convert silent_hours from UTC to project's timezone for display
      const projectTz = data.project.preferredTimezone || 'UTC'
      const silentHoursInProjectTz = convertSilentHoursFromUtc(data.project.silentHours, projectTz)

      // Parse time strings into hour and minute components
      const startParts = silentHoursInProjectTz.start
        ? silentHoursInProjectTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInProjectTz.end
        ? silentHoursInProjectTz.end.split(':')
        : [null, null]

      projectSilentHoursStartHour.value = startParts[0] || null
      originalProjectSilentHoursStartHour.value = startParts[0] || null
      projectSilentHoursStartMinute.value = startParts[1] || null
      originalProjectSilentHoursStartMinute.value = startParts[1] || null
      projectSilentHoursEndHour.value = endParts[0] || null
      originalProjectSilentHoursEndHour.value = endParts[0] || null
      projectSilentHoursEndMinute.value = endParts[1] || null
      originalProjectSilentHoursEndMinute.value = endParts[1] || null

      // Parse deadline report time
      const deadlineReportParts = data.project.deadlineReportTime
        ? data.project.deadlineReportTime.split(':')
        : [null, null]
      const deadlineReportDays = (data.project.deadlineReportDays || []).map(d => Number(d))
      deadlineReport.updateFromProps(
        deadlineReportParts[0] || null,
        deadlineReportParts[1] || null,
        deadlineReportDays
      )

      // Parse completed tasks report time
      const completedTasksReportParts = data.project.completedTasksReportTime
        ? data.project.completedTasksReportTime.split(':')
        : [null, null]
      const completedTasksReportDays = (data.project.completedTasksReportDays || []).map(d =>
        Number(d)
      )
      completedTasksReport.updateFromProps(
        completedTasksReportParts[0] || null,
        completedTasksReportParts[1] || null,
        completedTasksReportDays
      )
      projectRequiredFields.value = data.project.requiredFields || []
      originalProjectRequiredFields.value = data.project.requiredFields || []

      // Mark form as initialized after data is loaded
      isPopulatingForm.value = true
      formInitialized.value = true
      // Reset populating flag after a short delay to allow watchers to settle
      setTimeout(() => {
        isPopulatingForm.value = false
      }, 100)
    }
  },
  { immediate: true }
)

// Watch for lazy-loaded query results
watch(
  membersResult,
  data => {
    if (data?.project?.memberships) {
      memberships.value = mergeTelegramStateIntoMemberships(data.project.memberships || [])
    }
  },
  { immediate: true }
)

watch(
  membersTelegramStatusResult,
  data => {
    if (!['loading', 'success'].includes(telegramStatusLoadState.value)) return

    const telegramMemberships = data?.project?.memberships || []
    if (telegramMemberships.length === 0) return

    const nextState = { ...membershipTelegramStateById.value }
    telegramMemberships.forEach(membership => {
      nextState[String(membership.id)] = {
        inTelegramGroup: membership.inTelegramGroup,
        hasDmAccess: membership.hasDmAccess,
      }
    })

    membershipTelegramStateById.value = nextState
    memberships.value = mergeTelegramStateIntoMemberships(memberships.value)
  },
  { immediate: true }
)

watch(
  taskCategoriesResult,
  data => {
    if (data?.project?.taskCategories) {
      taskCategories.value = data.project.taskCategories || []
    }
  },
  { immediate: true }
)

watch(
  workflowsResult,
  data => {
    if (data?.project?.workflows) {
      workflows.value = data.project.workflows || []
    }
  },
  { immediate: true }
)

watch(
  gradingScalesResult,
  data => {
    if (data?.project?.gradingScales) {
      gradingScales.value = data.project.gradingScales || []
    }
  },
  { immediate: true }
)

// Watch for section expansion to trigger lazy loading
watch(
  () => expandedSections.value.members,
  expanded => {
    if (expanded && !membersResult.value && !membersLoading.value) {
      loadMembers()
    }
  }
)

watch(
  () => expandedSections.value.taskCategories,
  expanded => {
    if (expanded && !taskCategoriesResult.value && !taskCategoriesLoading.value) {
      loadTaskCategories()
    }
  }
)

watch(
  () => expandedSections.value.workflows,
  expanded => {
    if (expanded && !workflowsResult.value && !workflowsLoading.value) {
      loadWorkflows()
    }
  }
)

watch(
  () => expandedSections.value.gradingScales,
  expanded => {
    if (expanded && !gradingScalesResult.value && !gradingScalesLoading.value) {
      loadGradingScales()
    }
  }
)

// Combined refetch function for mutations
async function refetch() {
  const refetchPromises = [refetchBase()]

  // For lazy queries, use refetch() to force network fetch, or load() if not yet loaded
  if (expandedSections.value.members) {
    if (membersResult.value && refetchMembers) {
      refetchPromises.push(refetchMembers())
    } else if (!membersResult.value) {
      refetchPromises.push(loadMembers())
    }
    if (isTelegramStatusLoaded.value && refetchMembersTelegramStatus) {
      refetchPromises.push(refetchMembersTelegramStatus())
    }
  }
  if (expandedSections.value.taskCategories && taskCategoriesResult.value)
    refetchPromises.push(loadTaskCategories())
  if (expandedSections.value.workflows && workflowsResult.value)
    refetchPromises.push(loadWorkflows())
  if (expandedSections.value.gradingScales && gradingScalesResult.value)
    refetchPromises.push(loadGradingScales())

  await Promise.all(refetchPromises)
}

// GraphQL mutations
const CREATE_TASK_CATEGORY = gql`
  mutation CreateTaskCategory(
    $projectId: ID!
    $name: String!
    $workflowId: ID
    $gradingScaleId: ID
  ) {
    createTaskCategory(
      projectId: $projectId
      name: $name
      workflowId: $workflowId
      gradingScaleId: $gradingScaleId
    ) {
      taskCategory {
        id
        name
        workflow {
          id
          name
        }
        gradingScale {
          id
          name
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_CATEGORY = gql`
  mutation UpdateTaskCategory(
    $id: ID!
    $name: String!
    $workflowId: ID
    $gradingScaleId: ID
    $gradeMappings: [GradeMappingInput!]
    $workflowStatusMappings: [WorkflowStatusMappingInput!]
  ) {
    updateTaskCategory(
      id: $id
      name: $name
      workflowId: $workflowId
      gradingScaleId: $gradingScaleId
      gradeMappings: $gradeMappings
      workflowStatusMappings: $workflowStatusMappings
    ) {
      taskCategory {
        id
        name
        workflow {
          id
          name
        }
        gradingScale {
          id
          name
        }
      }
      errors
      warnings
      requiresGradeMappings
      requiresWorkflowStatusMappings
    }
  }
`

const DELETE_TASK_CATEGORY = gql`
  mutation DeleteTaskCategory($id: ID!, $transitionToTaskCategoryId: ID) {
    deleteTaskCategory(id: $id, transitionToTaskCategoryId: $transitionToTaskCategoryId) {
      taskCategory {
        id
      }
      errors
      warnings
      requiresTransitionTaskCategory
    }
  }
`

const UPDATE_MEMBER_ROLE = gql`
  mutation UpdateMemberRole($membershipId: ID!, $role: String!, $projectRole: String) {
    updateMemberRole(membershipId: $membershipId, role: $role, projectRole: $projectRole) {
      membership {
        id
        role
        projectRole
      }
      errors
    }
  }
`

const REMOVE_MEMBER = gql`
  mutation RemoveMember($membershipId: ID!, $reassignToUserId: ID) {
    removeMember(membershipId: $membershipId, reassignToUserId: $reassignToUserId) {
      membership {
        id
      }
      reassignedTasksCount
      userStillInGroup
      errors
    }
  }
`

const UPDATE_PROJECT = gql`
  mutation UpdateProject(
    $id: ID!
    $preferredLanguage: String
    $preferredTimezone: String
    $gradesEnabled: Boolean
    $verticalRelationsEnabled: Boolean
    $timeTrackingEnabled: Boolean
    $taskCloningEnabled: Boolean
    $boardsEnabled: Boolean
    $checklistsEnabled: Boolean
    $templatesEnabled: Boolean
    $groupChatNotificationsEnabled: Boolean
    $silentHours: JSON
    $deadlineReportTime: String
    $deadlineReportDays: [Int!]
    $completedTasksReportTime: String
    $completedTasksReportDays: [Int!]
    $requiredFields: [String!]
    $code: String
  ) {
    updateProject(
      id: $id
      preferredLanguage: $preferredLanguage
      preferredTimezone: $preferredTimezone
      gradesEnabled: $gradesEnabled
      verticalRelationsEnabled: $verticalRelationsEnabled
      timeTrackingEnabled: $timeTrackingEnabled
      taskCloningEnabled: $taskCloningEnabled
      boardsEnabled: $boardsEnabled
      checklistsEnabled: $checklistsEnabled
      templatesEnabled: $templatesEnabled
      groupChatNotificationsEnabled: $groupChatNotificationsEnabled
      silentHours: $silentHours
      deadlineReportTime: $deadlineReportTime
      deadlineReportDays: $deadlineReportDays
      completedTasksReportTime: $completedTasksReportTime
      completedTasksReportDays: $completedTasksReportDays
      requiredFields: $requiredFields
      code: $code
    ) {
      project {
        id
        code
        preferredLanguage
        preferredTimezone
        gradesEnabled
        verticalRelationsEnabled
        timeTrackingEnabled
        taskCloningEnabled
        boardsEnabled
        checklistsEnabled
        templatesEnabled
        groupChatNotificationsEnabled
        silentHours
        deadlineReportTime
        deadlineReportDays
        completedTasksReportTime
        completedTasksReportDays
        requiredFields
      }
      errors
    }
  }
`

const UPDATE_TITLE_MUTATION = gql`
  mutation UpdateProjectTitle($id: ID!, $title: String!) {
    updateProjectTitle(id: $id, title: $title) {
      project {
        id
        title
      }
      errors
    }
  }
`

const UPDATE_DESCRIPTION_MUTATION = gql`
  mutation UpdateProjectDescription($id: ID!, $description: String) {
    updateProjectDescription(id: $id, description: $description) {
      project {
        id
        description
      }
      errors
    }
  }
`

const { mutate: updateProjectMutation } = useMutation(UPDATE_PROJECT)
const { mutate: updateTitleMutation } = useMutation(UPDATE_TITLE_MUTATION)
const { mutate: updateDescriptionMutation } = useMutation(UPDATE_DESCRIPTION_MUTATION)
const { mutate: createTaskCategoryMutation } = useMutation(CREATE_TASK_CATEGORY)
const { mutate: updateTaskCategoryMutation } = useMutation(UPDATE_TASK_CATEGORY)
const { mutate: deleteTaskCategoryMutation } = useMutation(DELETE_TASK_CATEGORY)
const { mutate: updateMemberRoleMutation } = useMutation(UPDATE_MEMBER_ROLE)
const { mutate: removeMemberMutation } = useMutation(REMOVE_MEMBER)

// Convert silent hours from UTC to project's timezone for display
function convertSilentHoursFromUtc(silentHours, projectTimezone) {
  if (!silentHours || !silentHours.start || !silentHours.end) {
    return { start: null, end: null }
  }

  const tz = projectTimezone || 'UTC'

  // Parse UTC times and convert to project's timezone
  const startUtc = parseTimeString(silentHours.start)
  const endUtc = parseTimeString(silentHours.end)

  // Create Date objects for today with UTC times, then convert to project's timezone
  const today = new Date()
  const startDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      startUtc.hour,
      startUtc.minute
    )
  )
  const endDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      endUtc.hour,
      endUtc.minute
    )
  )

  // Format in project's timezone
  const startInTz = formatTimeInTimezone(startDate, tz)
  const endInTz = formatTimeInTimezone(endDate, tz)

  return { start: startInTz, end: endInTz }
}

function parseTimeString(timeStr) {
  const parts = timeStr.split(':')
  return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1] || '0', 10) }
}

function formatTimeInTimezone(date, timezone) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

function clearProjectSilentHours() {
  projectSilentHoursStartHour.value = null
  projectSilentHoursStartMinute.value = null
  projectSilentHoursEndHour.value = null
  projectSilentHoursEndMinute.value = null
}

function clearProjectDeadlineReport() {
  deadlineReport.clear()
}

function clearProjectCompletedTasksReport() {
  completedTasksReport.clear()
}

// Auto-set minutes to "00" when hour is selected and minutes is blank
watch(
  () => projectSilentHoursStartHour.value,
  newHour => {
    if (newHour && projectSilentHoursStartMinute.value === null) {
      projectSilentHoursStartMinute.value = '00'
    }
  }
)

watch(
  () => projectSilentHoursEndHour.value,
  newHour => {
    if (newHour && projectSilentHoursEndMinute.value === null) {
      projectSilentHoursEndMinute.value = '00'
    }
  }
)

// Watchers are handled by the composable

// Watch for Basic Settings changes and auto-save (excluding Silent Hours)
watch(
  () => [
    projectLanguage.value,
    projectTimezone.value,
    projectGradesEnabled.value,
    projectVerticalRelationsEnabled.value,
    projectTimeTrackingEnabled.value,
    projectTaskCloningEnabled.value,
    projectBoardsEnabled.value,
    projectChecklistsEnabled.value,
    projectTemplatesEnabled.value,
    projectGroupChatNotificationsEnabled.value,
  ],
  (newValues, oldValues) => {
    // Only auto-save if form has been initialized (project data loaded)
    if (!formInitialized.value) return

    // Don't auto-save if we're currently populating the form
    if (isPopulatingForm.value) return

    // Skip if oldValues is undefined (initial watch)
    if (!oldValues) return

    // Additional safety check: ensure we have a project
    if (!project.value || !props.projectId) return

    // Check if any value actually changed
    const hasChanges = newValues.some((val, index) => val !== oldValues[index])
    if (!hasChanges) return

    // Debounce auto-save to avoid too many requests
    if (autoSaveTimeout.value) {
      clearTimeout(autoSaveTimeout.value)
    }
    autoSaveTimeout.value = setTimeout(() => {
      autoSaveBasicSettings()
    }, 500)
  },
  { flush: 'post' }
)

// Watch for timezone changes and update silent hours display
watch(
  () => projectTimezone.value,
  (newTz, oldTz) => {
    // Only update if we have silent hours and timezone actually changed
    // Don't update if user has unsaved changes (check if current values differ from original)
    if (project.value?.silentHours && newTz !== oldTz && oldTz !== undefined) {
      // Check if user has unsaved changes - if so, don't overwrite them
      const hasUnsavedChanges =
        projectSilentHoursStartHour.value !== originalProjectSilentHoursStartHour.value ||
        projectSilentHoursStartMinute.value !== originalProjectSilentHoursStartMinute.value ||
        projectSilentHoursEndHour.value !== originalProjectSilentHoursEndHour.value ||
        projectSilentHoursEndMinute.value !== originalProjectSilentHoursEndMinute.value

      if (hasUnsavedChanges) {
        // User has unsaved changes, don't overwrite them
        return
      }

      const projectTz = newTz || 'UTC'
      const silentHoursInProjectTz = convertSilentHoursFromUtc(project.value.silentHours, projectTz)

      // Parse time strings into hour and minute components
      const startParts = silentHoursInProjectTz.start
        ? silentHoursInProjectTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInProjectTz.end
        ? silentHoursInProjectTz.end.split(':')
        : [null, null]

      projectSilentHoursStartHour.value = startParts[0] || null
      projectSilentHoursStartMinute.value = startParts[1] || null
      projectSilentHoursEndHour.value = endParts[0] || null
      projectSilentHoursEndMinute.value = endParts[1] || null

      // Also update original values to match
      originalProjectSilentHoursStartHour.value = startParts[0] || null
      originalProjectSilentHoursStartMinute.value = startParts[1] || null
      originalProjectSilentHoursEndHour.value = endParts[0] || null
      originalProjectSilentHoursEndMinute.value = endParts[1] || null
    }
  }
)

// Auto-save function for Basic Settings (excluding Silent Hours)
async function autoSaveBasicSettings() {
  if (!props.projectId || autoSaving.value || saving.value) return

  // Only save if form has been initialized (project data loaded)
  if (!project.value) return

  // Don't auto-save if we're currently populating the form
  if (isPopulatingForm.value) return

  autoSaving.value = true
  basicSettingsAlert.value = null

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      preferredLanguage: projectLanguage.value,
      preferredTimezone: projectTimezone.value,
      gradesEnabled: projectGradesEnabled.value,
      verticalRelationsEnabled: projectVerticalRelationsEnabled.value,
      timeTrackingEnabled: projectTimeTrackingEnabled.value,
      taskCloningEnabled: projectTaskCloningEnabled.value,
      boardsEnabled: projectBoardsEnabled.value,
      checklistsEnabled: projectChecklistsEnabled.value,
      templatesEnabled: projectTemplatesEnabled.value,
      groupChatNotificationsEnabled: projectGroupChatNotificationsEnabled.value,
    })

    if (data?.updateProject?.errors?.length > 0) {
      basicSettingsAlert.value = {
        type: 'error',
        title: s__('ProjectSettings|Error'),
        message: data.updateProject.errors.join(', '),
      }
    } else if (data?.updateProject?.project) {
      const updatedProject = data.updateProject.project

      // Update original values to reflect saved state
      originalProjectLanguage.value = updatedProject.preferredLanguage || 'en'
      originalProjectTimezone.value = updatedProject.preferredTimezone
      originalProjectGradesEnabled.value = updatedProject.gradesEnabled || false
      originalProjectVerticalRelationsEnabled.value =
        updatedProject.verticalRelationsEnabled || false
      originalProjectTimeTrackingEnabled.value = updatedProject.timeTrackingEnabled || false
      originalProjectTaskCloningEnabled.value = updatedProject.taskCloningEnabled || false
      originalProjectBoardsEnabled.value = updatedProject.boardsEnabled || false
      originalProjectChecklistsEnabled.value = updatedProject.checklistsEnabled || false
      originalProjectTemplatesEnabled.value = updatedProject.templatesEnabled || false
      originalProjectGroupChatNotificationsEnabled.value =
        updatedProject.groupChatNotificationsEnabled !== false

      // Update form values with returned data
      projectLanguage.value = updatedProject.preferredLanguage || 'en'
      projectTimezone.value = updatedProject.preferredTimezone
      projectGradesEnabled.value = updatedProject.gradesEnabled || false
      projectVerticalRelationsEnabled.value = updatedProject.verticalRelationsEnabled || false
      projectTimeTrackingEnabled.value = updatedProject.timeTrackingEnabled || false
      projectTaskCloningEnabled.value = updatedProject.taskCloningEnabled || false
      projectBoardsEnabled.value = updatedProject.boardsEnabled || false
      projectChecklistsEnabled.value = updatedProject.checklistsEnabled || false
      projectTemplatesEnabled.value = updatedProject.templatesEnabled || false
      projectGroupChatNotificationsEnabled.value =
        updatedProject.groupChatNotificationsEnabled !== false

      gradesEnabled.value = updatedProject.gradesEnabled || false

      basicSettingsAlert.value = {
        type: 'success',
        title: s__('ProjectSettings|Success'),
        message: s__('ProjectSettings|Settings saved successfully!'),
      }
    }
  } catch (err) {
    basicSettingsAlert.value = {
      type: 'error',
      title: s__('ProjectSettings|Error'),
      message: err.message || s__('ProjectSettings|Failed to save settings'),
    }
  } finally {
    autoSaving.value = false
  }
}

// Title editing functions
function startEditTitle() {
  editedTitle.value = project.value?.title || ''
  editingTitle.value = true
  titleError.value = ''
}

function cancelEditTitle() {
  editingTitle.value = false
  editedTitle.value = ''
  titleError.value = ''
}

async function saveTitle() {
  if (!editedTitle.value.trim()) {
    titleError.value = s__('ProjectSettings|Title cannot be empty')
    return
  }

  savingTitle.value = true
  titleError.value = ''

  try {
    const { data } = await updateTitleMutation({
      id: props.projectId,
      title: editedTitle.value.trim(),
    })

    if (data?.updateProjectTitle?.errors?.length) {
      throw new Error(data.updateProjectTitle.errors.join(', '))
    }

    // Update local project data
    await refetchBase()
    editingTitle.value = false
  } catch (e) {
    titleError.value = e.message || s__('ProjectSettings|Failed to update title')
  } finally {
    savingTitle.value = false
  }
}

async function saveCode() {
  if (!canSetCode.value) return

  const trimmedCode = editedCode.value.trim()
  const attemptedCode = trimmedCode
  if (!trimmedCode) {
    codeError.value = s__('ProjectSettings|Code cannot be empty')
    return
  }
  if (!CODE_PATTERN.test(trimmedCode)) {
    codeError.value = s__(
      'ProjectSettings|Code must be 3-12 chars, contain at least one latin letter, and use only letters, digits, "_" or "-".'
    )
    return
  }

  savingCode.value = true
  codeError.value = ''

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      code: trimmedCode,
    })

    if (data?.updateProject?.errors?.length) {
      // On validation errors Apollo may cache unsaved project attributes from mutation payload.
      // Refetch canonical server state to keep code input unlocked without a page reload.
      await refetchBase()
      if (!originalCode.value) {
        editedCode.value = attemptedCode
      }
      throw new Error(data.updateProject.errors.join(', '))
    }

    const savedCode = data?.updateProject?.project?.code || trimmedCode
    editedCode.value = savedCode
    originalCode.value = savedCode
    await refetchBase()
  } catch (e) {
    codeError.value = e.message || s__('ProjectSettings|Failed to update code')
  } finally {
    savingCode.value = false
  }
}

// Description editing functions
function startEditDescription() {
  editedDescription.value = project.value?.description || ''
  editingDescription.value = true
  descriptionError.value = ''
}

function cancelEditDescription() {
  editingDescription.value = false
  editedDescription.value = ''
  descriptionError.value = ''
}

async function saveDescription() {
  savingDescription.value = true
  descriptionError.value = ''

  try {
    const { data } = await updateDescriptionMutation({
      id: props.projectId,
      description: editedDescription.value.trim() || undefined,
    })

    if (data?.updateProjectDescription?.errors?.length) {
      throw new Error(data.updateProjectDescription.errors.join(', '))
    }

    // Update local project data
    await refetchBase()
    editingDescription.value = false
  } catch (e) {
    descriptionError.value = e.message || s__('ProjectSettings|Failed to update description')
  } finally {
    savingDescription.value = false
  }
}

// Save function for Silent Hours
async function saveSilentHours() {
  if (!props.projectId || savingSilentHours.value || saving.value) return

  savingSilentHours.value = true
  silentHoursAlert.value = null

  // Prepare silent_hours for submission (in project's timezone, backend will convert to UTC)
  const startHour = projectSilentHoursStartHour.value
    ? String(projectSilentHoursStartHour.value).padStart(2, '0')
    : null
  const startMinute =
    projectSilentHoursStartMinute.value !== null
      ? String(projectSilentHoursStartMinute.value).padStart(2, '0')
      : null
  const endHour = projectSilentHoursEndHour.value
    ? String(projectSilentHoursEndHour.value).padStart(2, '0')
    : null
  const endMinute =
    projectSilentHoursEndMinute.value !== null
      ? String(projectSilentHoursEndMinute.value).padStart(2, '0')
      : null

  const startTime = startHour && startMinute !== null ? `${startHour}:${startMinute}` : null
  const endTime = endHour && endMinute !== null ? `${endHour}:${endMinute}` : null

  const silentHours =
    startTime && endTime
      ? {
          start: startTime,
          end: endTime,
        }
      : {}

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      silentHours: silentHours,
    })

    if (data?.updateProject?.errors?.length > 0) {
      silentHoursAlert.value = {
        type: 'error',
        title: s__('ProjectSettings|Error'),
        message: data.updateProject.errors.join(', '),
      }
    } else if (data?.updateProject?.project) {
      const updatedProject = data.updateProject.project
      const projectTz = updatedProject.preferredTimezone || projectTimezone.value || 'UTC'
      const silentHoursInProjectTz = convertSilentHoursFromUtc(
        updatedProject.silentHours,
        projectTz
      )

      // Parse time strings into hour and minute components
      const startParts = silentHoursInProjectTz.start
        ? silentHoursInProjectTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInProjectTz.end
        ? silentHoursInProjectTz.end.split(':')
        : [null, null]

      projectSilentHoursStartHour.value = startParts[0] || null
      originalProjectSilentHoursStartHour.value = startParts[0] || null
      projectSilentHoursStartMinute.value = startParts[1] || null
      originalProjectSilentHoursStartMinute.value = startParts[1] || null
      projectSilentHoursEndHour.value = endParts[0] || null
      originalProjectSilentHoursEndHour.value = endParts[0] || null
      projectSilentHoursEndMinute.value = endParts[1] || null
      originalProjectSilentHoursEndMinute.value = endParts[1] || null

      silentHoursAlert.value = {
        type: 'success',
        title: s__('ProjectSettings|Success'),
        message: s__('ProjectSettings|Settings saved successfully!'),
      }
    }
  } catch (err) {
    silentHoursAlert.value = {
      type: 'error',
      title: s__('ProjectSettings|Error'),
      message: err.message || s__('ProjectSettings|Failed to save silent hours'),
    }
  } finally {
    savingSilentHours.value = false
  }
}

// Project settings actions
async function saveProjectSettings() {
  if (saving.value || !hasProjectChanges.value) return

  saving.value = true
  saveError.value = ''
  saveSuccess.value = false

  // Prepare silent_hours for submission (in project's timezone, backend will convert to UTC)
  // Combine hour and minute into HH:MM format
  // Send empty object if not set, so backend can clear it
  // Read current values directly from refs to ensure we have the latest
  // Ensure values are strings and properly formatted
  const startHour = projectSilentHoursStartHour.value
    ? String(projectSilentHoursStartHour.value).padStart(2, '0')
    : null
  const startMinute =
    projectSilentHoursStartMinute.value !== null
      ? String(projectSilentHoursStartMinute.value).padStart(2, '0')
      : null
  const endHour = projectSilentHoursEndHour.value
    ? String(projectSilentHoursEndHour.value).padStart(2, '0')
    : null
  const endMinute =
    projectSilentHoursEndMinute.value !== null
      ? String(projectSilentHoursEndMinute.value).padStart(2, '0')
      : null

  const startTime = startHour && startMinute !== null ? `${startHour}:${startMinute}` : null
  const endTime = endHour && endMinute !== null ? `${endHour}:${endMinute}` : null

  const silentHours =
    startTime && endTime
      ? {
          start: startTime,
          end: endTime,
        }
      : {}

  // Preserve current report values when saving general settings
  // Always send the values (even if null) so the backend can process them and clear the settings
  // Both hour and minute must be set (not null/undefined/empty) to create a valid time string
  const deadlineReportTime =
    projectDeadlineReportHour.value != null &&
    projectDeadlineReportHour.value !== '' &&
    projectDeadlineReportMinute.value != null &&
    projectDeadlineReportMinute.value !== ''
      ? `${String(projectDeadlineReportHour.value).padStart(2, '0')}:${String(projectDeadlineReportMinute.value).padStart(2, '0')}`
      : null
  // Always send the array, even if empty, so the backend can clear the days
  const deadlineReportDays = projectDeadlineReportDays.value
    ? projectDeadlineReportDays.value.map(d => parseInt(d, 10))
    : []
  const completedTasksReportTime =
    projectCompletedTasksReportHour.value != null &&
    projectCompletedTasksReportHour.value !== '' &&
    projectCompletedTasksReportMinute.value != null &&
    projectCompletedTasksReportMinute.value !== ''
      ? `${String(projectCompletedTasksReportHour.value).padStart(2, '0')}:${String(projectCompletedTasksReportMinute.value).padStart(2, '0')}`
      : null
  // Always send the array, even if empty, so the backend can clear the days
  const completedTasksReportDays = projectCompletedTasksReportDays.value
    ? projectCompletedTasksReportDays.value.map(d => parseInt(d, 10))
    : []

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      preferredLanguage: projectLanguage.value,
      preferredTimezone: projectTimezone.value,
      gradesEnabled: projectGradesEnabled.value,
      verticalRelationsEnabled: projectVerticalRelationsEnabled.value,
      timeTrackingEnabled: projectTimeTrackingEnabled.value,
      taskCloningEnabled: projectTaskCloningEnabled.value,
      boardsEnabled: projectBoardsEnabled.value,
      checklistsEnabled: projectChecklistsEnabled.value,
      templatesEnabled: projectTemplatesEnabled.value,
      groupChatNotificationsEnabled: projectGroupChatNotificationsEnabled.value,
      silentHours: silentHours,
      deadlineReportTime: deadlineReportTime, // Send null explicitly to clear, not undefined
      deadlineReportDays: deadlineReportDays, // Always send array, even if empty
      completedTasksReportTime: completedTasksReportTime, // Send null explicitly to clear, not undefined
      completedTasksReportDays: completedTasksReportDays, // Always send array, even if empty
    })

    if (data?.updateProject?.errors?.length > 0) {
      saveError.value = data.updateProject.errors.join(', ')
    } else if (data?.updateProject?.project) {
      originalProjectLanguage.value = projectLanguage.value
      originalProjectTimezone.value = projectTimezone.value
      originalProjectGradesEnabled.value = projectGradesEnabled.value
      originalProjectVerticalRelationsEnabled.value = projectVerticalRelationsEnabled.value
      originalProjectTimeTrackingEnabled.value = projectTimeTrackingEnabled.value
      originalProjectTaskCloningEnabled.value = projectTaskCloningEnabled.value
      originalProjectBoardsEnabled.value = projectBoardsEnabled.value
      originalProjectChecklistsEnabled.value = projectChecklistsEnabled.value
      originalProjectTemplatesEnabled.value = projectTemplatesEnabled.value
      originalProjectGroupChatNotificationsEnabled.value =
        projectGroupChatNotificationsEnabled.value

      // Update silent hours from returned data (including converted from UTC)
      const updatedProject = data.updateProject.project
      const projectTz = updatedProject.preferredTimezone || projectTimezone.value || 'UTC'
      const silentHoursInProjectTz = convertSilentHoursFromUtc(
        updatedProject.silentHours,
        projectTz
      )

      // Parse time strings into hour and minute components
      const startParts = silentHoursInProjectTz.start
        ? silentHoursInProjectTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInProjectTz.end
        ? silentHoursInProjectTz.end.split(':')
        : [null, null]

      projectSilentHoursStartHour.value = startParts[0] || null
      originalProjectSilentHoursStartHour.value = startParts[0] || null
      projectSilentHoursStartMinute.value = startParts[1] || null
      originalProjectSilentHoursStartMinute.value = startParts[1] || null
      projectSilentHoursEndHour.value = endParts[0] || null
      originalProjectSilentHoursEndHour.value = endParts[0] || null
      projectSilentHoursEndMinute.value = endParts[1] || null
      originalProjectSilentHoursEndMinute.value = endParts[1] || null

      // Update gradesEnabled to reflect the change
      gradesEnabled.value = data.updateProject.project.gradesEnabled || false
      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 3000)
    }
  } catch (err) {
    saveError.value = err.message || 'Failed to save project settings'
  } finally {
    saving.value = false
  }
}

// Handle project archived event from ArchiveProject component
function handleProjectArchived(archivedProject) {
  // Update local state
  projectArchived.value = true
  if (project.value) {
    project.value.archived = true
  }

  // Refetch to get updated data
  refetchBase()
}

// Regular Reports actions
async function saveRegularReports() {
  if (savingReports.value || !hasRegularReportsChanges.value) return

  savingReports.value = true
  saveReportsError.value = ''
  saveReportsSuccess.value = false

  // Get values from composables
  const deadlineReportValues = deadlineReport.getValues()
  const completedTasksReportValues = completedTasksReport.getValues()

  // Prepare deadline report time for submission
  // Send empty string if time is cleared (nullified) so backend can clear the setting
  // Both hour and minute must be set (not null/undefined/empty) to create a valid time string
  const deadlineReportTime =
    deadlineReportValues.hour != null &&
    deadlineReportValues.hour !== '' &&
    deadlineReportValues.minute != null &&
    deadlineReportValues.minute !== ''
      ? `${String(deadlineReportValues.hour).padStart(2, '0')}:${String(deadlineReportValues.minute).padStart(2, '0')}`
      : ''

  // Prepare deadline report days (convert to integers)
  // Always send the array, even if empty, so the backend can clear the days
  const deadlineReportDays = deadlineReportValues.days.map(d => parseInt(d, 10))

  // Prepare completed tasks report time for submission
  // Send empty string if time is cleared (nullified) so backend can clear the setting
  // Both hour and minute must be set (not null/undefined/empty) to create a valid time string
  const completedTasksReportTime =
    completedTasksReportValues.hour != null &&
    completedTasksReportValues.hour !== '' &&
    completedTasksReportValues.minute != null &&
    completedTasksReportValues.minute !== ''
      ? `${String(completedTasksReportValues.hour).padStart(2, '0')}:${String(completedTasksReportValues.minute).padStart(2, '0')}`
      : ''

  // Prepare completed tasks report days (convert to integers)
  // Always send the array, even if empty, so the backend can clear the days
  const completedTasksReportDays = completedTasksReportValues.days.map(d => parseInt(d, 10))

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      preferredLanguage: undefined,
      preferredTimezone: undefined,
      gradesEnabled: undefined,
      verticalRelationsEnabled: undefined,
      timeTrackingEnabled: undefined,
      groupChatNotificationsEnabled: undefined,
      silentHours: undefined,
      deadlineReportTime: deadlineReportTime, // Send null explicitly to clear, not undefined
      deadlineReportDays: deadlineReportDays, // Always send array, even if empty
      completedTasksReportTime: completedTasksReportTime, // Send null explicitly to clear, not undefined
      completedTasksReportDays: completedTasksReportDays, // Always send array, even if empty
    })

    if (data?.updateProject?.errors?.length > 0) {
      saveReportsError.value = data.updateProject.errors.join(', ')
    } else if (data?.updateProject?.project) {
      const updatedProject = data.updateProject.project

      // Parse deadline report time
      const deadlineReportParts = updatedProject.deadlineReportTime
        ? updatedProject.deadlineReportTime.split(':')
        : [null, null]
      const deadlineReportDays = (updatedProject.deadlineReportDays || []).map(d => Number(d))
      deadlineReport.updateFromProps(
        deadlineReportParts[0] || null,
        deadlineReportParts[1] || null,
        deadlineReportDays
      )

      // Parse completed tasks report time
      const completedTasksReportParts = updatedProject.completedTasksReportTime
        ? updatedProject.completedTasksReportTime.split(':')
        : [null, null]
      const completedTasksReportDays = (updatedProject.completedTasksReportDays || []).map(d =>
        Number(d)
      )
      completedTasksReport.updateFromProps(
        completedTasksReportParts[0] || null,
        completedTasksReportParts[1] || null,
        completedTasksReportDays
      )

      saveReportsSuccess.value = true
      setTimeout(() => {
        saveReportsSuccess.value = false
      }, 3000)
    }
  } catch (err) {
    saveReportsError.value = err.message || 'Failed to save regular reports settings'
  } finally {
    savingReports.value = false
  }
}

// Required Fields actions
async function saveRequiredFields() {
  if (savingRequiredFields.value || !hasRequiredFieldsChanges.value) return

  savingRequiredFields.value = true
  saveRequiredFieldsError.value = ''
  saveRequiredFieldsSuccess.value = false

  try {
    const { data } = await updateProjectMutation({
      id: props.projectId,
      preferredLanguage: undefined,
      preferredTimezone: undefined,
      gradesEnabled: undefined,
      verticalRelationsEnabled: undefined,
      timeTrackingEnabled: undefined,
      taskCloningEnabled: undefined,
      boardsEnabled: undefined,
      checklistsEnabled: undefined,
      templatesEnabled: undefined,
      groupChatNotificationsEnabled: undefined,
      silentHours: undefined,
      deadlineReportTime: undefined,
      deadlineReportDays: undefined,
      completedTasksReportTime: undefined,
      completedTasksReportDays: undefined,
      requiredFields: projectRequiredFields.value || [],
    })

    if (data?.updateProject?.errors?.length > 0) {
      saveRequiredFieldsError.value = data.updateProject.errors.join(', ')
    } else if (data?.updateProject?.project) {
      const updatedProject = data.updateProject.project
      projectRequiredFields.value = updatedProject.requiredFields || []
      originalProjectRequiredFields.value = updatedProject.requiredFields || []

      saveRequiredFieldsSuccess.value = true
      setTimeout(() => {
        saveRequiredFieldsSuccess.value = false
      }, 3000)
    }
  } catch (err) {
    saveRequiredFieldsError.value = err.message || 'Failed to save required fields settings'
  } finally {
    savingRequiredFields.value = false
  }
}

// Member actions - handler for removing members with tasks (from MembersList component)
function handleRemoveMemberWithTasks(membership) {
  removingMembership.value = membership
  reassignToUserId.value = null
  showMemberRemovalReassignmentModal.value = true
}

function handleMembersListAlert(alertData) {
  membersListAlert.value = alertData
}

async function executeMemberRemoval(membershipId, reassignToUserIdValue) {
  try {
    const { data } = await removeMemberMutation({
      membershipId: membershipId,
      reassignToUserId: reassignToUserIdValue || null,
    })
    if (data?.removeMember?.errors?.length) {
      throw new Error(data.removeMember.errors.join(', '))
    }

    const reassignedCount = data?.removeMember?.reassignedTasksCount || 0
    const userStillInGroup = data?.removeMember?.userStillInGroup || false

    let message = s__('ProjectSettings|Member removed.')
    if (reassignedCount > 0) {
      message +=
        ' ' +
        sprintf(s__('ProjectSettings|%{count} task(s) were reassigned.'), {
          count: reassignedCount,
        })
    }
    if (userStillInGroup) {
      message +=
        '\n\n' +
        s__(
          'ProjectSettings|⚠️ Warning: This user is still in the Telegram group and can easily rejoin the project.'
        )
    }

    alert(message)

    await refetch()
  } catch (e) {
    alert('Failed to remove member: ' + (e.message || 'Unknown error'))
  }
}

function closeMemberRemovalReassignmentModal() {
  showMemberRemovalReassignmentModal.value = false
  removingMembership.value = null
  reassignToUserId.value = null
}

async function confirmMemberRemoval() {
  if (!removingMembership.value) return

  const membershipId = removingMembership.value.id
  const reassignToUserIdValue = reassignToUserId.value || null

  closeMemberRemovalReassignmentModal()
  await executeMemberRemoval(membershipId, reassignToUserIdValue)
}

// Computed: available members for reassignment (excluding the one being removed)
const availableMembersForReassignment = computed(() => {
  if (!removingMembership.value) return []
  return memberships.value.filter(m => m.id !== removingMembership.value.id)
})

// Computed: mapped users for reassignment (with role)
const availableUsersForReassignment = computed(() => {
  return availableMembersForReassignment.value.map(m => ({ ...m.user, role: m.role }))
})

// Task type actions
async function editTaskCategory(tt) {
  editingTaskCategory.value = {
    id: tt.id,
    name: tt.name,
    workflowId: tt.workflow?.id,
    gradingScaleId: tt.gradingScale?.id || null,
  }
  originalGradingScaleId.value = tt.gradingScale?.id || null
  originalWorkflowId.value = tt.workflow?.id || null
  showNewTaskCategoryForm.value = false

  // Ensure workflows and grading scales are loaded for the dropdowns
  if (!workflowsResult.value) {
    await loadWorkflows()
  }
  if (gradesEnabled.value && !gradingScalesResult.value) {
    await loadGradingScales()
  }
}

function cancelEditTaskCategory() {
  editingTaskCategory.value = null
  originalGradingScaleId.value = null
  originalWorkflowId.value = null
  taskCategoryError.value = ''
  showGradeTransitionModal.value = false
  gradeMappings.value = {}
  showWorkflowStatusTransitionModal.value = false
  workflowStatusMappings.value = {}
}

function cancelNewTaskCategory() {
  showNewTaskCategoryForm.value = false
  newTaskCategory.value = { name: '', workflowId: undefined, gradingScaleId: null }
  taskCategoryError.value = ''
}

// Watch for when the new task category form is shown to load workflows and grading scales
watch(showNewTaskCategoryForm, async show => {
  if (show) {
    // Ensure workflows and grading scales are loaded for the dropdowns
    if (!workflowsResult.value) {
      await loadWorkflows()
    }
    if (gradesEnabled.value && !gradingScalesResult.value) {
      await loadGradingScales()
    }
  }
})

async function createTaskCategory() {
  taskCategoryError.value = ''
  submittingTaskCategory.value = true
  try {
    const { data } = await createTaskCategoryMutation({
      projectId: props.projectId,
      name: newTaskCategory.value.name,
      workflowId: newTaskCategory.value.workflowId || undefined,
      gradingScaleId: newTaskCategory.value.gradingScaleId || undefined,
    })
    if (data?.createTaskCategory?.errors?.length) {
      throw new Error(data.createTaskCategory.errors.join(', '))
    }

    // Add the new task category to the list if section is expanded
    if (data?.createTaskCategory?.taskCategory && expandedSections.value.taskCategories) {
      taskCategories.value = [...taskCategories.value, data.createTaskCategory.taskCategory]
    }

    cancelNewTaskCategory()

    // Refetch task categories if section is expanded
    if (expandedSections.value.taskCategories) {
      await loadTaskCategories()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    taskCategoryError.value = e.message || 'Failed to create task category'
  } finally {
    submittingTaskCategory.value = false
  }
}

async function updateTaskCategory() {
  taskCategoryError.value = ''
  submittingTaskCategory.value = true

  const oldGradingScaleId = originalGradingScaleId.value
  const newGradingScaleId = editingTaskCategory.value.gradingScaleId
  const oldWorkflowId = originalWorkflowId.value
  const newWorkflowId = editingTaskCategory.value.workflowId

  // Check if workflow is changing
  // Don't show modal here - let backend check if there are tasks
  // Backend will return requiresWorkflowStatusMappings: true only if there are tasks
  const workflowChanging = oldWorkflowId !== newWorkflowId

  // Check if grading scale is changing
  const gradingScaleChanging = oldGradingScaleId !== newGradingScaleId

  if (gradingScaleChanging) {
    const oldScale = oldGradingScaleId
      ? gradingScales.value.find(gs => gs.id === oldGradingScaleId)
      : null
    const newScale = newGradingScaleId
      ? gradingScales.value.find(gs => gs.id === newGradingScaleId)
      : null

    // If switching to None or empty scale, show warning (only if there might be tasks)
    // Note: We'll let the backend check if there are actually tasks with grades
    // and show the modal only if required
    if (!newScale || !newScale.grades || newScale.grades.length === 0) {
      // For now, proceed - backend will handle the check
      // If there are no tasks, no warning needed
    } else if (
      oldScale &&
      oldScale.grades &&
      oldScale.grades.length > 0 &&
      newScale &&
      newScale.grades &&
      newScale.grades.length > 0
    ) {
      // Switching from one scale to another
      // Don't show modal here - let backend check if there are tasks with grades
      // Backend will return requiresGradeMappings: true only if there are tasks with grades
    }
  }

  // Proceed with update
  await performUpdateTaskCategory()
}

async function performUpdateTaskCategory(
  providedGradeMappings = null,
  providedWorkflowStatusMappings = null
) {
  try {
    // Convert grade mappings to GraphQL format
    const gradeMappingsToUse = providedGradeMappings || gradeMappings.value
    const gradeMappingsInput = Object.keys(gradeMappingsToUse)
      .filter(
        key =>
          gradeMappingsToUse[key] != null &&
          gradeMappingsToUse[key] !== '' &&
          gradeMappingsToUse[key] !== undefined
      )
      .map(oldGradeId => ({
        oldGradeId: oldGradeId.toString(),
        newGradeId: gradeMappingsToUse[oldGradeId].toString(),
      }))

    // Convert workflow status mappings to GraphQL format
    const workflowStatusMappingsToUse =
      providedWorkflowStatusMappings || workflowStatusMappings.value
    const workflowStatusMappingsInput = Object.keys(workflowStatusMappingsToUse)
      .filter(
        key =>
          workflowStatusMappingsToUse[key] != null &&
          workflowStatusMappingsToUse[key] !== '' &&
          workflowStatusMappingsToUse[key] !== undefined
      )
      .map(oldStatusId => ({
        oldStatusId: oldStatusId.toString(),
        newStatusId: workflowStatusMappingsToUse[oldStatusId].toString(),
      }))

    const { data } = await updateTaskCategoryMutation({
      id: editingTaskCategory.value.id,
      name: editingTaskCategory.value.name,
      workflowId: editingTaskCategory.value.workflowId,
      gradingScaleId: editingTaskCategory.value.gradingScaleId || undefined,
      gradeMappings: gradeMappingsInput.length > 0 ? gradeMappingsInput : null,
      workflowStatusMappings:
        workflowStatusMappingsInput.length > 0 ? workflowStatusMappingsInput : null,
    })

    if (data?.updateTaskCategory?.requiresWorkflowStatusMappings) {
      // Only show modal if workflow is actually changing
      const oldWorkflowId = originalWorkflowId.value
      const newWorkflowId = editingTaskCategory.value.workflowId
      const workflowChanging = oldWorkflowId !== newWorkflowId

      if (workflowChanging) {
        // Need to show workflow status mapping modal (backend detected tasks that need mapping)
        const oldWorkflow = oldWorkflowId
          ? workflows.value.find(wf => wf.id === oldWorkflowId)
          : null
        const newWorkflow = newWorkflowId
          ? workflows.value.find(wf => wf.id === newWorkflowId)
          : null
        if (oldWorkflow && newWorkflow) {
          transitionOldWorkflow.value = oldWorkflow
          transitionNewWorkflow.value = newWorkflow

          // Default mapping: map statuses by position (0->0, 1->1, etc.)
          workflowStatusMappings.value = {}
          const sortedOld = [...oldWorkflow.statuses].sort((a, b) => a.position - b.position)
          const sortedNew = [...newWorkflow.statuses].sort((a, b) => a.position - b.position)
          sortedOld.forEach((oldStatus, index) => {
            if (sortedNew[index]) {
              workflowStatusMappings.value[oldStatus.id] = sortedNew[index].id
            }
          })

          showWorkflowStatusTransitionModal.value = true
          submittingTaskCategory.value = false
          return
        }
      }
    }

    if (data?.updateTaskCategory?.requiresGradeMappings) {
      // Backend detected that there are tasks with grades that need mapping
      // Only show modal if grading scale is actually changing
      const oldGradingScaleId = originalGradingScaleId.value
      const newGradingScaleId = editingTaskCategory.value.gradingScaleId
      const gradingScaleChanging = oldGradingScaleId !== newGradingScaleId

      if (gradingScaleChanging) {
        // Need to show grade mapping modal (backend detected task grades that need mapping)
        const oldScale = oldGradingScaleId
          ? gradingScales.value.find(gs => gs.id === oldGradingScaleId)
          : null
        const newScale = newGradingScaleId
          ? gradingScales.value.find(gs => gs.id === newGradingScaleId)
          : null
        if (
          oldScale &&
          newScale &&
          oldScale.grades &&
          oldScale.grades.length > 0 &&
          newScale.grades &&
          newScale.grades.length > 0
        ) {
          transitionOldScale.value = oldScale
          transitionNewScale.value = newScale
          transitionTaskCategory.value = editingTaskCategory.value

          // Default mapping: map grades by position (0->0, 1->1, etc.)
          gradeMappings.value = {}
          const sortedOld = [...oldScale.grades].sort((a, b) => a.position - b.position)
          const sortedNew = [...newScale.grades].sort((a, b) => a.position - b.position)
          sortedOld.forEach((oldGrade, index) => {
            if (sortedNew[index]) {
              gradeMappings.value[oldGrade.id] = sortedNew[index].id
            }
          })

          showGradeTransitionModal.value = true
          submittingTaskCategory.value = false
          return
        }
      }
    }

    if (data?.updateTaskCategory?.errors?.length) {
      throw new Error(data.updateTaskCategory.errors.join(', '))
    }

    // Show warnings if any
    if (data?.updateTaskCategory?.warnings?.length) {
      alert('⚠️ ' + data.updateTaskCategory.warnings.join('\n'))
    }

    // Update the task category in the list if section is expanded
    if (data?.updateTaskCategory?.taskCategory && expandedSections.value.taskCategories) {
      const index = taskCategories.value.findIndex(
        tc => tc.id === data.updateTaskCategory.taskCategory.id
      )
      if (index !== -1) {
        taskCategories.value = [
          ...taskCategories.value.slice(0, index),
          data.updateTaskCategory.taskCategory,
          ...taskCategories.value.slice(index + 1),
        ]
      }
    }

    cancelEditTaskCategory()

    // Refetch task categories if section is expanded
    if (expandedSections.value.taskCategories) {
      await loadTaskCategories()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    taskCategoryError.value = e.message || 'Failed to update task category'
  } finally {
    submittingTaskCategory.value = false
  }
}

function closeGradeTransitionModal() {
  showGradeTransitionModal.value = false
  gradeMappings.value = {}
  transitionOldScale.value = null
  transitionNewScale.value = null
  transitionTaskCategory.value = null
}

async function confirmGradeTransition() {
  // Validate that all old grades are mapped
  if (!transitionOldScale.value || !transitionNewScale.value) return

  const oldGrades = sortedOldGrades.value
  const unmappedGrades = oldGrades.filter(
    grade =>
      !gradeMappings.value[grade.id] ||
      gradeMappings.value[grade.id] === null ||
      gradeMappings.value[grade.id] === ''
  )

  if (unmappedGrades.length > 0) {
    alert(
      sprintf(s__('ProjectSettings|Please map all grades. Unmapped: %{grades}'), {
        grades: unmappedGrades.map(g => g.value).join(', '),
      })
    )
    return
  }

  // Store mappings before closing modal (which clears them)
  const mappingsToUse = { ...gradeMappings.value }

  closeGradeTransitionModal()
  submittingTaskCategory.value = true
  await performUpdateTaskCategory(mappingsToUse, null)
}

function closeWorkflowStatusTransitionModal() {
  showWorkflowStatusTransitionModal.value = false
  workflowStatusMappings.value = {}
  transitionOldWorkflow.value = null
  transitionNewWorkflow.value = null
}

async function confirmWorkflowStatusTransition() {
  // Validate that all old statuses are mapped
  if (!transitionOldWorkflow.value || !transitionNewWorkflow.value) return

  const oldStatuses = sortedOldStatuses.value
  const unmappedStatuses = oldStatuses.filter(
    status =>
      !workflowStatusMappings.value[status.id] ||
      workflowStatusMappings.value[status.id] === null ||
      workflowStatusMappings.value[status.id] === ''
  )

  if (unmappedStatuses.length > 0) {
    alert(
      sprintf(s__('ProjectSettings|Please map all workflow statuses. Unmapped: %{statuses}'), {
        statuses: unmappedStatuses.map(s => s.name).join(', '),
      })
    )
    return
  }

  // Store mappings before closing modal (which clears them)
  const mappingsToUse = { ...workflowStatusMappings.value }

  closeWorkflowStatusTransitionModal()
  submittingTaskCategory.value = true
  await performUpdateTaskCategory(null, mappingsToUse)
}

async function deleteTaskCategory(tt) {
  const confirmed = await showConfirm(`Delete task category "${tt.name}"?`)
  if (!confirmed) return

  try {
    // Ensure task categories are loaded for the transition modal if needed
    if (!taskCategoriesResult.value) {
      await loadTaskCategories()
    }

    const { data } = await deleteTaskCategoryMutation({ id: tt.id })

    if (data?.deleteTaskCategory?.requiresTransitionTaskCategory) {
      // Show modal to select transition task category
      deletingTaskCategory.value = tt
      transitionToTaskCategoryId.value = null
      showTaskCategoryDeletionTransitionModal.value = true
      return
    }

    if (data?.deleteTaskCategory?.errors?.length) {
      throw new Error(data.deleteTaskCategory.errors.join(', '))
    }

    // Show warnings if any
    if (data?.deleteTaskCategory?.warnings?.length) {
      alert('⚠️ ' + data.deleteTaskCategory.warnings.join('\n'))
    }

    // Remove the task category from the list if section is expanded
    if (expandedSections.value.taskCategories) {
      taskCategories.value = taskCategories.value.filter(tc => tc.id !== tt.id)
    }

    // Refetch task categories if section is expanded
    if (expandedSections.value.taskCategories) {
      await loadTaskCategories()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    alert('Failed to delete task category: ' + (e.message || 'Unknown error'))
  }
}

function closeTaskCategoryDeletionTransitionModal() {
  showTaskCategoryDeletionTransitionModal.value = false
  deletingTaskCategory.value = null
  transitionToTaskCategoryId.value = null
}

async function confirmTaskCategoryDeletion() {
  if (!deletingTaskCategory.value || !transitionToTaskCategoryId.value) {
    alert(s__('ProjectSettings|Please select a task category to transition tasks to.'))
    return
  }

  try {
    const { data } = await deleteTaskCategoryMutation({
      id: deletingTaskCategory.value.id,
      transitionToTaskCategoryId: transitionToTaskCategoryId.value,
    })

    if (data?.deleteTaskCategory?.errors?.length) {
      throw new Error(data.deleteTaskCategory.errors.join(', '))
    }

    // Show warnings if any
    if (data?.deleteTaskCategory?.warnings?.length) {
      alert('⚠️ ' + data.deleteTaskCategory.warnings.join('\n'))
    }

    // Remove the task category from the list if section is expanded
    if (deletingTaskCategory.value && expandedSections.value.taskCategories) {
      taskCategories.value = taskCategories.value.filter(
        tc => tc.id !== deletingTaskCategory.value.id
      )
    }

    closeTaskCategoryDeletionTransitionModal()

    // Refetch task categories if section is expanded
    if (expandedSections.value.taskCategories) {
      await loadTaskCategories()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    alert('Failed to delete task category: ' + (e.message || 'Unknown error'))
  }
}

// Workflow GraphQL mutations
const CREATE_WORKFLOW = gql`
  mutation CreateWorkflow(
    $projectId: ID!
    $name: String!
    $description: String
    $isDefault: Boolean
  ) {
    createWorkflow(
      projectId: $projectId
      name: $name
      description: $description
      isDefault: $isDefault
    ) {
      workflow {
        id
        name
        description
        isDefault
      }
      errors
    }
  }
`

const UPDATE_WORKFLOW = gql`
  mutation UpdateWorkflow($id: ID!, $name: String!, $description: String, $isDefault: Boolean) {
    updateWorkflow(id: $id, name: $name, description: $description, isDefault: $isDefault) {
      workflow {
        id
        name
        description
        isDefault
      }
      errors
    }
  }
`

const DELETE_WORKFLOW = gql`
  mutation DeleteWorkflow($id: ID!) {
    deleteWorkflow(id: $id) {
      workflow {
        id
      }
      errors
    }
  }
`

const CREATE_WORKFLOW_STATUS = gql`
  mutation CreateWorkflowStatus(
    $workflowId: ID!
    $name: String!
    $description: String
    $position: Int
    $color: String
    $isInitial: Boolean
    $isFinal: Boolean
    $requiresCompletedChecklists: Boolean
  ) {
    createWorkflowStatus(
      workflowId: $workflowId
      name: $name
      description: $description
      position: $position
      color: $color
      isInitial: $isInitial
      isFinal: $isFinal
      requiresCompletedChecklists: $requiresCompletedChecklists
    ) {
      workflowStatus {
        id
        name
        description
        position
        color
        isInitial
        isFinal
        requiresCompletedChecklists
      }
      errors
    }
  }
`

const UPDATE_WORKFLOW_STATUS = gql`
  mutation UpdateWorkflowStatus(
    $id: ID!
    $name: String
    $description: String
    $position: Int
    $color: String
    $isInitial: Boolean
    $isFinal: Boolean
    $requiresCompletedChecklists: Boolean
  ) {
    updateWorkflowStatus(
      id: $id
      name: $name
      description: $description
      position: $position
      color: $color
      isInitial: $isInitial
      isFinal: $isFinal
      requiresCompletedChecklists: $requiresCompletedChecklists
    ) {
      workflowStatus {
        id
        name
        description
        position
        color
        isInitial
        isFinal
        requiresCompletedChecklists
      }
      errors
    }
  }
`

const DELETE_WORKFLOW_STATUS = gql`
  mutation DeleteWorkflowStatus($id: ID!, $transitionToStatusId: ID) {
    deleteWorkflowStatus(id: $id, transitionToStatusId: $transitionToStatusId) {
      workflowStatus {
        id
      }
      errors
      warnings
      requiresTransitionStatus
    }
  }
`

const CREATE_WORKFLOW_TRANSITION = gql`
  mutation CreateWorkflowTransition(
    $workflowId: ID!
    $sourceStatusIds: [ID!]
    $targetStatusId: ID!
    $name: String
    $isGlobal: Boolean
  ) {
    createWorkflowTransition(
      workflowId: $workflowId
      sourceStatusIds: $sourceStatusIds
      targetStatusId: $targetStatusId
      name: $name
      isGlobal: $isGlobal
    ) {
      workflowTransition {
        id
        name
        isGlobal
        sourceStatuses {
          id
          name
        }
        targetStatus {
          id
          name
        }
      }
      errors
    }
  }
`

const DELETE_WORKFLOW_TRANSITION = gql`
  mutation DeleteWorkflowTransition($id: ID!) {
    deleteWorkflowTransition(id: $id) {
      workflowTransition {
        id
      }
      errors
    }
  }
`

const { mutate: createWorkflowMutation } = useMutation(CREATE_WORKFLOW)
const { mutate: updateWorkflowMutation } = useMutation(UPDATE_WORKFLOW)
const { mutate: deleteWorkflowMutation } = useMutation(DELETE_WORKFLOW)
const { mutate: createWorkflowStatusMutation } = useMutation(CREATE_WORKFLOW_STATUS)
const { mutate: updateWorkflowStatusMutation } = useMutation(UPDATE_WORKFLOW_STATUS)
const { mutate: deleteWorkflowStatusMutation } = useMutation(DELETE_WORKFLOW_STATUS)
const { mutate: createWorkflowTransitionMutation } = useMutation(CREATE_WORKFLOW_TRANSITION)
const { mutate: deleteWorkflowTransitionMutation } = useMutation(DELETE_WORKFLOW_TRANSITION)

// Grading scale GraphQL mutations
const CREATE_GRADING_SCALE = gql`
  mutation CreateGradingScale($projectId: ID!, $name: String!, $position: Int) {
    createGradingScale(projectId: $projectId, name: $name, position: $position) {
      gradingScale {
        id
        name
        position
      }
      errors
    }
  }
`

const UPDATE_GRADING_SCALE = gql`
  mutation UpdateGradingScale($id: ID!, $name: String, $position: Int) {
    updateGradingScale(id: $id, name: $name, position: $position) {
      gradingScale {
        id
        name
        position
      }
      errors
    }
  }
`

const DESTROY_GRADING_SCALE = gql`
  mutation DestroyGradingScale($id: ID!) {
    destroyGradingScale(id: $id) {
      success
      errors
      warnings
    }
  }
`

const CREATE_GRADE = gql`
  mutation CreateGrade(
    $gradingScaleId: ID!
    $value: String!
    $description: String
    $position: Int
  ) {
    createGrade(
      gradingScaleId: $gradingScaleId
      value: $value
      description: $description
      position: $position
    ) {
      grade {
        id
        value
        description
        position
        gradingScaleId
      }
      errors
    }
  }
`

const UPDATE_GRADE = gql`
  mutation UpdateGrade($id: ID!, $value: String, $description: String, $position: Int) {
    updateGrade(id: $id, value: $value, description: $description, position: $position) {
      grade {
        id
        value
        description
        position
        gradingScaleId
      }
      errors
    }
  }
`

const DESTROY_GRADE = gql`
  mutation DestroyGrade($id: ID!) {
    destroyGrade(id: $id) {
      success
      errors
      warnings
    }
  }
`

const { mutate: createGradingScaleMutation } = useMutation(CREATE_GRADING_SCALE)
const { mutate: updateGradingScaleMutation } = useMutation(UPDATE_GRADING_SCALE)
const { mutate: destroyGradingScaleMutation } = useMutation(DESTROY_GRADING_SCALE)
const { mutate: createGradeMutation } = useMutation(CREATE_GRADE)
const { mutate: updateGradeMutation } = useMutation(UPDATE_GRADE)
const { mutate: destroyGradeMutation } = useMutation(DESTROY_GRADE)

// Workflow actions
function viewWorkflow(wf) {
  viewingWorkflow.value = { ...wf }
  showNewWorkflowForm.value = false
  editingWorkflow.value = null
}

function closeViewWorkflow() {
  viewingWorkflow.value = null
  showNewStatusForm.value = false
  editingStatus.value = null
  showNewTransitionForm.value = false
}

function editWorkflow(wf) {
  editingWorkflow.value = { ...wf }
  showNewWorkflowForm.value = false
  viewingWorkflow.value = null
}

function cancelEditWorkflow() {
  editingWorkflow.value = null
  workflowError.value = ''
}

function cancelNewWorkflow() {
  showNewWorkflowForm.value = false
  newWorkflow.value = { name: '', description: '', isDefault: false }
  workflowError.value = ''
}

async function createWorkflow() {
  workflowError.value = ''
  submittingWorkflow.value = true
  try {
    const { data } = await createWorkflowMutation({
      projectId: props.projectId,
      name: newWorkflow.value.name,
      description: newWorkflow.value.description || null,
      isDefault: newWorkflow.value.isDefault,
    })
    if (data?.createWorkflow?.errors?.length) {
      throw new Error(data.createWorkflow.errors.join(', '))
    }

    // Add the new workflow to the list if section is expanded
    if (data?.createWorkflow?.workflow && expandedSections.value.workflows) {
      // Initialize with empty arrays for statuses and transitions
      const newWorkflowData = {
        ...data.createWorkflow.workflow,
        statuses: [],
        transitions: [],
      }
      if (newWorkflowData.isDefault) {
        workflows.value = workflows.value.map(w => ({ ...w, isDefault: false }))
      }
      workflows.value = [...workflows.value, newWorkflowData]
    }

    cancelNewWorkflow()

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    workflowError.value = e.message || 'Failed to create workflow'
  } finally {
    submittingWorkflow.value = false
  }
}

async function updateWorkflow() {
  workflowError.value = ''
  submittingWorkflow.value = true
  try {
    const { data } = await updateWorkflowMutation({
      id: editingWorkflow.value.id,
      name: editingWorkflow.value.name,
      description: editingWorkflow.value.description || null,
      isDefault: editingWorkflow.value.isDefault,
    })
    if (data?.updateWorkflow?.errors?.length) {
      throw new Error(data.updateWorkflow.errors.join(', '))
    }

    // Update the workflow in the list if section is expanded
    if (data?.updateWorkflow?.workflow && expandedSections.value.workflows) {
      if (data.updateWorkflow.workflow.isDefault) {
        workflows.value = workflows.value.map(w => ({ ...w, isDefault: false }))
      }
      const index = workflows.value.findIndex(w => w.id === data.updateWorkflow.workflow.id)
      if (index !== -1) {
        // Preserve statuses and transitions
        const existingWorkflow = workflows.value[index]
        workflows.value = [
          ...workflows.value.slice(0, index),
          {
            ...data.updateWorkflow.workflow,
            statuses: existingWorkflow.statuses || [],
            transitions: existingWorkflow.transitions || [],
          },
          ...workflows.value.slice(index + 1),
        ]
      }
    }

    cancelEditWorkflow()

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    workflowError.value = e.message || 'Failed to update workflow'
  } finally {
    submittingWorkflow.value = false
  }
}

async function deleteWorkflow(wf) {
  const confirmed = await showConfirm(`Delete workflow "${wf.name}"?`)
  if (!confirmed) return
  try {
    const { data } = await deleteWorkflowMutation({ id: wf.id })
    if (data?.deleteWorkflow?.errors?.length) {
      throw new Error(data.deleteWorkflow.errors.join(', '))
    }

    // Remove the workflow from the list if section is expanded
    if (expandedSections.value.workflows) {
      workflows.value = workflows.value.filter(w => w.id !== wf.id)
    }

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    alert('Failed to delete workflow: ' + (e.message || 'Unknown error'))
  }
}

// Status actions
function editStatus(status) {
  editingStatus.value = { ...status }
  showNewStatusForm.value = false
}

function cancelEditStatus() {
  editingStatus.value = null
  statusError.value = ''
}

function cancelNewStatus() {
  showNewStatusForm.value = false
  newStatus.value = {
    name: '',
    description: '',
    position: 0,
    color: '#6B7280',
    isInitial: false,
    isFinal: false,
    requiresCompletedChecklists: false,
  }
  statusError.value = ''
}

async function createStatus() {
  statusError.value = ''
  submittingStatus.value = true
  try {
    const { data } = await createWorkflowStatusMutation({
      workflowId: viewingWorkflow.value.id,
      name: newStatus.value.name,
      description: newStatus.value.description || null,
      position: newStatus.value.position,
      color: newStatus.value.color,
      isInitial: newStatus.value.isInitial,
      isFinal: newStatus.value.isFinal,
      requiresCompletedChecklists: newStatus.value.requiresCompletedChecklists,
    })
    if (data?.createWorkflowStatus?.errors?.length) {
      throw new Error(data.createWorkflowStatus.errors.join(', '))
    }

    // Add the new status to the workflow's statuses array
    if (data?.createWorkflowStatus?.workflowStatus && expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        workflows.value = [
          ...workflows.value.slice(0, workflowIndex),
          {
            ...workflow,
            statuses: [...(workflow.statuses || []), data.createWorkflowStatus.workflowStatus],
          },
          ...workflows.value.slice(workflowIndex + 1),
        ]
        // Update viewingWorkflow
        viewingWorkflow.value = { ...workflows.value[workflowIndex] }
      }
    }

    cancelNewStatus()

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    statusError.value = e.message || 'Failed to create status'
  } finally {
    submittingStatus.value = false
  }
}

async function updateStatus() {
  statusError.value = ''
  submittingStatus.value = true
  try {
    const { data } = await updateWorkflowStatusMutation({
      id: editingStatus.value.id,
      name: editingStatus.value.name,
      description: editingStatus.value.description || null,
      position: editingStatus.value.position,
      color: editingStatus.value.color,
      isInitial: editingStatus.value.isInitial,
      isFinal: editingStatus.value.isFinal,
      requiresCompletedChecklists: editingStatus.value.requiresCompletedChecklists,
    })
    if (data?.updateWorkflowStatus?.errors?.length) {
      throw new Error(data.updateWorkflowStatus.errors.join(', '))
    }

    // Update the status in the workflow's statuses array
    if (data?.updateWorkflowStatus?.workflowStatus && expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        const statusIndex = (workflow.statuses || []).findIndex(
          s => s.id === data.updateWorkflowStatus.workflowStatus.id
        )
        if (statusIndex !== -1) {
          workflows.value = [
            ...workflows.value.slice(0, workflowIndex),
            {
              ...workflow,
              statuses: [
                ...workflow.statuses.slice(0, statusIndex),
                data.updateWorkflowStatus.workflowStatus,
                ...workflow.statuses.slice(statusIndex + 1),
              ],
            },
            ...workflows.value.slice(workflowIndex + 1),
          ]
          // Update viewingWorkflow
          viewingWorkflow.value = { ...workflows.value[workflowIndex] }
        }
      }
    }

    cancelEditStatus()

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    statusError.value = e.message || 'Failed to update status'
  } finally {
    submittingStatus.value = false
  }
}

async function deleteStatus(status) {
  const confirmed = await showConfirm(`Delete status "${status.name}"?`)
  if (!confirmed) return

  try {
    const { data } = await deleteWorkflowStatusMutation({ id: status.id })

    if (data?.deleteWorkflowStatus?.requiresTransitionStatus) {
      // Show modal to select transition status
      deletingStatus.value = status
      transitionToStatusId.value = null
      showStatusDeletionTransitionModal.value = true
      return
    }

    if (data?.deleteWorkflowStatus?.errors?.length) {
      throw new Error(data.deleteWorkflowStatus.errors.join(', '))
    }

    // Show warnings if any
    if (data?.deleteWorkflowStatus?.warnings?.length) {
      alert('⚠️ ' + data.deleteWorkflowStatus.warnings.join('\n'))
    }

    // Remove the status from the workflow's statuses array
    if (expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        workflows.value = [
          ...workflows.value.slice(0, workflowIndex),
          {
            ...workflow,
            statuses: (workflow.statuses || []).filter(s => s.id !== status.id),
          },
          ...workflows.value.slice(workflowIndex + 1),
        ]
        // Update viewingWorkflow
        viewingWorkflow.value = { ...workflows.value[workflowIndex] }
      }
    }

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    alert('Failed to delete status: ' + (e.message || 'Unknown error'))
  }
}

function closeStatusDeletionTransitionModal() {
  showStatusDeletionTransitionModal.value = false
  deletingStatus.value = null
  transitionToStatusId.value = null
}

async function confirmStatusDeletion() {
  if (!deletingStatus.value || !transitionToStatusId.value) {
    alert(s__('ProjectSettings|Please select a status to transition tasks to.'))
    return
  }

  try {
    const { data } = await deleteWorkflowStatusMutation({
      id: deletingStatus.value.id,
      transitionToStatusId: transitionToStatusId.value,
    })

    if (data?.deleteWorkflowStatus?.errors?.length) {
      throw new Error(data.deleteWorkflowStatus.errors.join(', '))
    }

    // Show warnings if any
    if (data?.deleteWorkflowStatus?.warnings?.length) {
      alert('⚠️ ' + data.deleteWorkflowStatus.warnings.join('\n'))
    }

    // Remove the status from the workflow's statuses array
    if (deletingStatus.value && expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        workflows.value = [
          ...workflows.value.slice(0, workflowIndex),
          {
            ...workflow,
            statuses: (workflow.statuses || []).filter(s => s.id !== deletingStatus.value.id),
          },
          ...workflows.value.slice(workflowIndex + 1),
        ]
        // Update viewingWorkflow
        viewingWorkflow.value = { ...workflows.value[workflowIndex] }
      }
    }

    closeStatusDeletionTransitionModal()

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    alert('Failed to delete status: ' + (e.message || 'Unknown error'))
  }
}

// Transition actions
function cancelNewTransition() {
  showNewTransitionForm.value = false
  newTransition.value = { sourceStatusIds: [], targetStatusId: '', name: '', isGlobal: false }
  transitionError.value = ''
}

async function createTransition() {
  transitionError.value = ''
  submittingTransition.value = true
  try {
    const { data } = await createWorkflowTransitionMutation({
      workflowId: viewingWorkflow.value.id,
      sourceStatusIds: newTransition.value.isGlobal ? null : newTransition.value.sourceStatusIds,
      targetStatusId: newTransition.value.targetStatusId,
      name: newTransition.value.name || null,
      isGlobal: newTransition.value.isGlobal,
    })
    if (data?.createWorkflowTransition?.errors?.length) {
      throw new Error(data.createWorkflowTransition.errors.join(', '))
    }

    // Add the new transition to the workflow's transitions array with complete data from mutation
    if (data?.createWorkflowTransition?.workflowTransition && expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        // Use the complete transition data from mutation which includes sourceStatuses
        const newTransition = data.createWorkflowTransition.workflowTransition

        // Ensure sourceStatuses is an array (defensive check)
        if (!Array.isArray(newTransition.sourceStatuses)) {
          newTransition.sourceStatuses = []
        }

        workflows.value = [
          ...workflows.value.slice(0, workflowIndex),
          {
            ...workflow,
            transitions: [...(workflow.transitions || []), newTransition],
          },
          ...workflows.value.slice(workflowIndex + 1),
        ]
        // Update viewingWorkflow with the updated workflow that includes the new transition
        viewingWorkflow.value = { ...workflows.value[workflowIndex] }
      }
    }

    cancelNewTransition()

    // Refetch workflows to ensure we have the latest data from server
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data from server
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    transitionError.value = e.message || 'Failed to create transition'
  } finally {
    submittingTransition.value = false
  }
}

async function deleteTransition(transition) {
  let description = ''
  if (transition.isGlobal) {
    description = `global transition to "${transition.targetStatus?.name}"`
  } else if (transition.sourceStatuses?.length === 1) {
    description = `transition from "${transition.sourceStatuses[0].name}" to "${transition.targetStatus?.name}"`
  } else if (transition.sourceStatuses?.length > 1) {
    const sources = transition.sourceStatuses.map(s => s.name).join(', ')
    description = `transition from [${sources}] to "${transition.targetStatus?.name}"`
  } else {
    description = 'this transition'
  }
  const confirmed = await showConfirm(`Delete ${description}?`)
  if (!confirmed) return
  try {
    const { data } = await deleteWorkflowTransitionMutation({ id: transition.id })
    if (data?.deleteWorkflowTransition?.errors?.length) {
      throw new Error(data.deleteWorkflowTransition.errors.join(', '))
    }

    // Remove the transition from the workflow's transitions array
    if (expandedSections.value.workflows) {
      const workflowIndex = workflows.value.findIndex(w => w.id === viewingWorkflow.value.id)
      if (workflowIndex !== -1) {
        const workflow = workflows.value[workflowIndex]
        workflows.value = [
          ...workflows.value.slice(0, workflowIndex),
          {
            ...workflow,
            transitions: (workflow.transitions || []).filter(t => t.id !== transition.id),
          },
          ...workflows.value.slice(workflowIndex + 1),
        ]
        // Update viewingWorkflow
        viewingWorkflow.value = { ...workflows.value[workflowIndex] }
      }
    }

    // Refetch workflows if section is expanded
    if (expandedSections.value.workflows) {
      await loadWorkflows()
      // Update viewingWorkflow with fresh data
      const updatedWorkflow = workflows.value.find(w => w.id === viewingWorkflow.value.id)
      if (updatedWorkflow) viewingWorkflow.value = { ...updatedWorkflow }
    }
  } catch (e) {
    alert('Failed to delete transition: ' + (e.message || 'Unknown error'))
  }
}

// Grading scale actions
function viewGradingScale(gs) {
  viewingGradingScale.value = { ...gs }
  showNewGradingScaleForm.value = false
  editingGradingScale.value = null
}

function closeViewGradingScale() {
  viewingGradingScale.value = null
  showNewGradeForm.value = false
  editingGrade.value = null
}

function editGradingScale(gs) {
  editingGradingScale.value = { ...gs }
  showNewGradingScaleForm.value = false
  viewingGradingScale.value = null
}

function cancelEditGradingScale() {
  editingGradingScale.value = null
  gradingScaleError.value = ''
}

function cancelNewGradingScale() {
  showNewGradingScaleForm.value = false
  newGradingScale.value = { name: '' }
  gradingScaleError.value = ''
}

async function createGradingScale() {
  gradingScaleError.value = ''
  submittingGradingScale.value = true
  try {
    const { data } = await createGradingScaleMutation({
      projectId: props.projectId,
      name: newGradingScale.value.name,
    })
    if (data?.createGradingScale?.errors?.length) {
      throw new Error(data.createGradingScale.errors.join(', '))
    }

    // Add the new grading scale to the list if section is expanded
    if (data?.createGradingScale?.gradingScale && expandedSections.value.gradingScales) {
      // Initialize with empty array for grades
      const newGradingScaleData = {
        ...data.createGradingScale.gradingScale,
        grades: [],
      }
      gradingScales.value = [...gradingScales.value, newGradingScaleData]
    }

    cancelNewGradingScale()

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    gradingScaleError.value = e.message || 'Failed to create grading scale'
  } finally {
    submittingGradingScale.value = false
  }
}

async function updateGradingScale() {
  gradingScaleError.value = ''
  submittingGradingScale.value = true
  try {
    const { data } = await updateGradingScaleMutation({
      id: editingGradingScale.value.id,
      name: editingGradingScale.value.name,
    })
    if (data?.updateGradingScale?.errors?.length) {
      throw new Error(data.updateGradingScale.errors.join(', '))
    }

    // Update the grading scale in the list if section is expanded
    if (data?.updateGradingScale?.gradingScale && expandedSections.value.gradingScales) {
      const index = gradingScales.value.findIndex(
        gs => gs.id === data.updateGradingScale.gradingScale.id
      )
      if (index !== -1) {
        // Preserve grades
        const existingScale = gradingScales.value[index]
        gradingScales.value = [
          ...gradingScales.value.slice(0, index),
          {
            ...data.updateGradingScale.gradingScale,
            grades: existingScale.grades || [],
          },
          ...gradingScales.value.slice(index + 1),
        ]
      }
    }

    cancelEditGradingScale()

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    gradingScaleError.value = e.message || 'Failed to update grading scale'
  } finally {
    submittingGradingScale.value = false
  }
}

async function deleteGradingScale(gs) {
  // First, check if there are warnings by attempting deletion (but we'll show confirmation first)
  // We'll show a generic warning in the confirmation, then show specific warnings after if any
  let confirmMessage = sprintf(s__('ProjectSettings|Delete grading scale "%{name}"?'), {
    name: gs.name,
  })
  confirmMessage +=
    '\n\n⚠️ ' +
    s__(
      'ProjectSettings|Warning: If this grading scale has grades assigned to tasks, all task grades will be deleted.'
    )

  const confirmed = await showConfirm(confirmMessage)
  if (!confirmed) return

  try {
    const { data } = await destroyGradingScaleMutation({ id: gs.id })
    if (data?.destroyGradingScale?.errors?.length) {
      throw new Error(data.destroyGradingScale.errors.join(', '))
    }

    // Show warnings if any (informational, deletion already happened)
    if (data?.destroyGradingScale?.warnings?.length) {
      alert('⚠️ ' + data.destroyGradingScale.warnings.join('\n'))
    }

    // Remove the grading scale from the list if section is expanded
    if (expandedSections.value.gradingScales) {
      gradingScales.value = gradingScales.value.filter(scale => scale.id !== gs.id)
    }

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
    }

    // Also refetch base query
    await refetchBase()
  } catch (e) {
    alert(
      sprintf(s__('ProjectSettings|Failed to delete grading scale: %{error}'), {
        error: e.message || s__('ProjectSettings|Unknown error'),
      })
    )
  }
}

// Grade actions
function editGrade(grade) {
  editingGrade.value = { ...grade }
  showNewGradeForm.value = false
}

function cancelEditGrade() {
  editingGrade.value = null
  gradeError.value = ''
}

function cancelNewGrade() {
  showNewGradeForm.value = false
  newGrade.value = { value: '', description: '' }
  gradeError.value = ''
}

async function createGrade() {
  gradeError.value = ''
  submittingGrade.value = true
  try {
    const { data } = await createGradeMutation({
      gradingScaleId: viewingGradingScale.value.id,
      value: newGrade.value.value,
      description: newGrade.value.description || null,
    })
    if (data?.createGrade?.errors?.length) {
      throw new Error(data.createGrade.errors.join(', '))
    }

    // Add the new grade to the grading scale's grades array
    if (data?.createGrade?.grade && expandedSections.value.gradingScales) {
      const scaleIndex = gradingScales.value.findIndex(gs => gs.id === viewingGradingScale.value.id)
      if (scaleIndex !== -1) {
        const scale = gradingScales.value[scaleIndex]
        gradingScales.value = [
          ...gradingScales.value.slice(0, scaleIndex),
          {
            ...scale,
            grades: [...(scale.grades || []), data.createGrade.grade],
          },
          ...gradingScales.value.slice(scaleIndex + 1),
        ]
        // Update viewingGradingScale
        viewingGradingScale.value = { ...gradingScales.value[scaleIndex] }
      }
    }

    cancelNewGrade()

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
      // Update viewingGradingScale with fresh data
      const updatedScale = gradingScales.value.find(gs => gs.id === viewingGradingScale.value.id)
      if (updatedScale) viewingGradingScale.value = { ...updatedScale }
    }
  } catch (e) {
    gradeError.value = e.message || 'Failed to create grade'
  } finally {
    submittingGrade.value = false
  }
}

async function updateGrade() {
  gradeError.value = ''
  submittingGrade.value = true
  try {
    const { data } = await updateGradeMutation({
      id: editingGrade.value.id,
      value: editingGrade.value.value,
      description: editingGrade.value.description || null,
    })
    if (data?.updateGrade?.errors?.length) {
      throw new Error(data.updateGrade.errors.join(', '))
    }

    // Update the grade in the grading scale's grades array
    if (data?.updateGrade?.grade && expandedSections.value.gradingScales) {
      const scaleIndex = gradingScales.value.findIndex(gs => gs.id === viewingGradingScale.value.id)
      if (scaleIndex !== -1) {
        const scale = gradingScales.value[scaleIndex]
        const gradeIndex = (scale.grades || []).findIndex(g => g.id === data.updateGrade.grade.id)
        if (gradeIndex !== -1) {
          gradingScales.value = [
            ...gradingScales.value.slice(0, scaleIndex),
            {
              ...scale,
              grades: [
                ...scale.grades.slice(0, gradeIndex),
                data.updateGrade.grade,
                ...scale.grades.slice(gradeIndex + 1),
              ],
            },
            ...gradingScales.value.slice(scaleIndex + 1),
          ]
          // Update viewingGradingScale
          viewingGradingScale.value = { ...gradingScales.value[scaleIndex] }
        }
      }
    }

    cancelEditGrade()

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
      // Update viewingGradingScale with fresh data
      const updatedScale = gradingScales.value.find(gs => gs.id === viewingGradingScale.value.id)
      if (updatedScale) viewingGradingScale.value = { ...updatedScale }
    }
  } catch (e) {
    gradeError.value = e.message || 'Failed to update grade'
  } finally {
    submittingGrade.value = false
  }
}

async function deleteGrade(grade) {
  // Show warning in confirmation dialog
  let confirmMessage = sprintf(s__('ProjectSettings|Delete grade "%{value}"?'), {
    value: grade.value,
  })
  confirmMessage +=
    '\n\n⚠️ ' +
    s__(
      'ProjectSettings|Warning: If this grade is assigned to any tasks, all task grades will be deleted.'
    )

  const confirmed = await showConfirm(confirmMessage)
  if (!confirmed) return

  try {
    const { data } = await destroyGradeMutation({ id: grade.id })
    if (data?.destroyGrade?.errors?.length) {
      throw new Error(data.destroyGrade.errors.join(', '))
    }

    // Show warnings if any (informational, deletion already happened)
    if (data?.destroyGrade?.warnings?.length) {
      alert('⚠️ ' + data.destroyGrade.warnings.join('\n'))
    }

    // Remove the grade from the grading scale's grades array
    if (expandedSections.value.gradingScales) {
      const scaleIndex = gradingScales.value.findIndex(gs => gs.id === viewingGradingScale.value.id)
      if (scaleIndex !== -1) {
        const scale = gradingScales.value[scaleIndex]
        gradingScales.value = [
          ...gradingScales.value.slice(0, scaleIndex),
          {
            ...scale,
            grades: (scale.grades || []).filter(g => g.id !== grade.id),
          },
          ...gradingScales.value.slice(scaleIndex + 1),
        ]
        // Update viewingGradingScale
        viewingGradingScale.value = { ...gradingScales.value[scaleIndex] }
      }
    }

    // Refetch grading scales if section is expanded
    if (expandedSections.value.gradingScales) {
      await loadGradingScales()
      // Update viewingGradingScale with fresh data
      const updatedScale = gradingScales.value.find(gs => gs.id === viewingGradingScale.value.id)
      if (updatedScale) viewingGradingScale.value = { ...updatedScale }
    }
  } catch (e) {
    alert(
      sprintf(s__('ProjectSettings|Failed to delete grade: %{error}'), {
        error: e.message || s__('ProjectSettings|Unknown error'),
      })
    )
  }
}
</script>

<style>
@import '../../styles/forms.css';
@import '../../styles/dashboard.css';
@import '../../styles/settings.css';
</style>
<style scoped>
.settings-page-content {
  min-width: 0;
}

.mobile-settings-nav-toggle {
  display: none;
}

.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
}

.settings-modal-content {
  max-width: 760px;
}

.settings-modal-content-wide {
  max-width: 1000px;
}

.feature-flags-block-title {
  margin: 8px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.task-type-item,
.task-category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.task-type-form,
.task-category-form {
  padding: 16px;
  background: white;
  border-radius: 4px;
  margin-top: 12px;
}

/* Button styles inherit from forms.css */

.workflow-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.workflow-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.workflow-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.workflow-title {
  min-width: 0;
  overflow-wrap: anywhere;
}

.workflow-item-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
}

.workflow-stats {
  font-size: 13px;
  color: #6c757d;
  margin-top: 4px;
}

.badge-default {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  background: #28a745;
  color: white;
  border-radius: 3px;
  text-transform: uppercase;
}

.workflow-form {
  padding: 16px;
  background: white;
  border-radius: 4px;
  margin-top: 12px;
}

.workflow-details {
  padding: 16px;
  background: white;
  border-radius: 4px;
  margin-top: 12px;
}

.workflow-section {
  padding: 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  margin-bottom: 16px;
}

.workflow-section:last-child {
  margin-bottom: 0;
}

.status-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px;
  background: white;
  border-radius: 4px;
  margin-bottom: 6px;
  border-left: 3px solid #007bff;
}

.status-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.status-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.status-title {
  min-width: 0;
  overflow-wrap: anywhere;
}

.status-badges-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.status-item-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.badge-initial {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  background: #17a2b8;
  color: white;
  border-radius: 3px;
  text-transform: uppercase;
}

.badge-final {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  background: #6c757d;
  color: white;
  border-radius: 3px;
  text-transform: uppercase;
}

.badge-checklist {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  background: #28a745;
  color: white;
  border-radius: 3px;
  text-transform: uppercase;
}

@media (max-width: 768px) {
  .workflow-title-row,
  .status-title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

@media (max-width: 767px) {
  .project-title-display-row .project-edit-title-btn,
  .project-description-display-row .project-edit-description-btn {
    margin-left: auto;
  }
}

.status-form {
  padding: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #dee2e6;
}

.transition-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px;
  background: white;
  border-radius: 4px;
  margin-bottom: 6px;
}

.transition-item-main {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.transition-item-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.transition-target-line {
  display: inline-flex;
  align-items: center;
}

.transition-arrow {
  margin: 0 8px;
}

.transition-name-primary {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.transition-form {
  padding: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #dee2e6;
}

.task-type-badge,
.task-category-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #e9ecef;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.badge-global {
  display: inline-block;
  padding: 2px 8px;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  background: #ffc107;
  color: #000;
  border-radius: 3px;
  text-transform: uppercase;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.role-select {
  padding: 4px 8px;
  font-size: 13px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.role-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  text-transform: capitalize;
}

.role-owner {
  background: #ffc107;
  color: #000;
}

.role-admin {
  background: #007bff;
  color: white;
}

.role-member {
  background: #6c757d;
  color: white;
}

.status-form input[type='color'] {
  width: 100%;
  height: 38px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
}

.title-edit-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.single-line-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Back button */
.btn-back {
  display: inline-block;
  padding: 8px 16px;
  background-color: #f5f5f5;
  color: #666;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.btn-back:hover {
  background-color: #e5e5e5;
}

/* Button styles inherit from forms.css */

@media (max-width: 640px) {
  .btn-back {
    min-width: 36px;
    padding: 8px;
    text-align: center;
  }
}

.success {
  color: #2e7d32;
  font-size: 14px;
}

@media (max-width: 767px) {
  .title-edit-row {
    flex-direction: column;
    align-items: stretch;
  }

  .transition-target-line {
    display: flex;
    margin-top: 4px;
  }

  .transition-arrow {
    margin: 0 6px 0 0;
  }
}

.deadline-report-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f0f7ff;
  border: 1px solid #d0e7ff;
  border-radius: 8px;
}

.deadline-report-time-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.deadline-report-days-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clear-deadline-report-btn {
  padding: 8px 12px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  opacity: 0.6;
  transition: all 0.2s;
  align-self: flex-start;
}

.clear-deadline-report-btn.active {
  background: #e3f2fd;
  border-color: #2196f3;
  color: #1976d2;
  opacity: 1;
}

.clear-deadline-report-btn:disabled {
  cursor: not-allowed;
}

.clear-deadline-report-btn:not(:disabled):hover {
  opacity: 1;
}

.grading-scale-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.grading-scale-stats {
  font-size: 13px;
  color: #6c757d;
  margin-top: 4px;
}

.grading-scale-form {
  padding: 16px;
  background: white;
  border-radius: 4px;
  margin-top: 12px;
}

.grading-scale-details {
  padding: 16px;
  background: white;
  border-radius: 4px;
  margin-top: 12px;
}

.grading-scale-section {
  padding: 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  margin-bottom: 16px;
}

.grading-scale-section:last-child {
  margin-bottom: 0;
}

.grade-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: white;
  border-radius: 4px;
  margin-bottom: 6px;
  border-left: 3px solid #007bff;
}

.grade-form {
  padding: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #dee2e6;
}

/* Modal styles */
/* Component-specific: z-index override */
.modal-overlay {
  z-index: 10000;
}

.setup-link {
  color: #1d6bf3;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.setup-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
}

.required-fields-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.required-fields-option-row {
  width: 100%;
}

.required-fields-option {
  display: flex !important;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.required-fields-option input[type='checkbox'] {
  margin: 0;
  width: 18px;
  min-width: 18px;
  max-width: 18px;
  height: 18px;
  min-height: 18px;
  max-height: 18px;
  flex-shrink: 0;
}

.required-fields-option span {
  display: block;
  line-height: 1.35;
}

@media (max-width: 767px) {
  .required-fields-list {
    gap: 0;
  }
}

@media (max-width: 1023px) {
  .mobile-settings-nav-toggle {
    display: inline-flex;
    position: sticky;
    top: 12px;
    z-index: 20;
    margin-bottom: 10px;
  }
}
</style>
